import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import type {
    User,
    BiAdminUser,
    UserAssignmentRow,
    AssignmentCompany,
    AssignmentSector,
    AssignmentDepartment,
    AssignmentWarehouse,
    StructurePayload,
    LoanCorrectionRow,
    LoanCorrectionPayload,
    ImpersonationPayload,
} from '../types/biApp';
import type { ModuleKey } from '../shared/moduleTitle';
import { moduleTitle } from '../shared/moduleTitle';
import { BI_MODULE_KEYS } from '../shared/biModuleKeys';
import { api, uploadFile } from '../shared/api';
import { formatMoney, formatNumber, formatDate, formatDateTime } from '../shared/formatting';
import { matchesQuickSearch, QuickSearchInput } from '../shared/quickSearch';
import { MultiSelectDropdown } from '../shared/MultiSelectDropdown';
import { RowMenu } from '../shared/RowMenu';
import { AdminCreateCard } from '../shared/AdminCreateCard';
import { ADMIN_ROLE_OPTIONS } from '../shared/constants';
import { Avatar } from './profileModule';
import { normalizeNumberArray } from '../shared/foundationFilters';
import {
    type AdminSectionId,
    type AdminThemeId,
    adminSectionDefinition,
    adminSectionNavLabel,
    childSectionsForUser,
    landingCardsForUser,
    themeSectionsForUser,
    visibleThemesForUser,
    parseAdminSectionFromSearch,
    syncAdminSectionUrl,
    userMayAccessAdminSection,
} from '../shared/adminSectionRegistry';
import { orderedAssignmentKeys, assignmentKeyLabel } from '../shared/granularModuleKeys';
import { AdminInvoiceObligationsSection } from './adminInvoiceObligationsSection';
import { AdminFinFacilitiesSection } from './adminFinFacilitiesSection';
import { AdminGoldGuaranteesSection } from './adminGoldGuaranteesSection';
import { AdminProductsSection } from './adminProductsSection';
import { AdminPurchaseOrderCodesSection } from './adminPurchaseOrderCodesSection';
import { AdminPartnersSection } from './adminPartnersSection';
import { AdminPartnerMappingSection } from './adminPartnerMappingSection';
import { AdminDocDocSection } from './adminDocDocSection';
import { AdminPeriodLocksSection } from './adminPeriodLocksSection';
import { AdminGoldWarehousesSection } from './adminGoldWarehousesSection';
import { AdminGoldSuppliersSection } from './adminGoldSuppliersSection';
import { AdminGoldModuleCategoriesSection } from './adminGoldModuleCategoriesSection';
import { AdminBankAccountsSection } from './adminBankAccountsSection';
import { AdminIngestSection } from './adminIngestSection';
import { AdminImportAuditSection } from './adminImportAuditSection';
import { AdminAuditLogSection } from './adminAuditLogSection';
import { AdminNotificationRecipientsSection } from './adminNotificationRecipientsSection';
import { AdminActivitySection } from './adminActivitySection';
import { ModuleNotesLogSection } from './moduleNotesLogSection';
import { AdminE2PersonsSection } from './adminE2PersonsSection';
import { AdminE2ProjectsSection } from './adminE2ProjectsSection';

type UserSortKey = 'name' | 'email' | 'company' | 'department' | 'home_wh' | 'modules' | 'role' | 'status' | 'activity' | 'protected';

export function AdminModule({
    token,
    currentUser,
    setUser,
    locale,
    onSessionReplace,
    onNavigateToCashflow,
}: {
    token: string;
    currentUser: User;
    setUser: React.Dispatch<React.SetStateAction<User | null>>;
    locale: 'hr' | 'en';
    onSessionReplace: (payload: { token: string; user: User; impersonation: ImpersonationPayload }) => void;
    onNavigateToCashflow?: (opts: { companyIds: number[]; dateFrom?: string; dateTo?: string }) => void;
}) {
    const isSuperadmin = currentUser.role === 'superadministrator';
    const isOwner = String(currentUser.email ?? '').toLowerCase() === 'vjakomin@gmail.com';
    // Strukturne admin sekcije (sektori, odjeli, proizvodi, partneri…) dostupne su
    // administratorima/superadminima ali i ne-administratorima koji su kroz modul 104
    // dobili odgovarajući granul (m.102, m.103, m.110, m.111…).
    const canManageStructure = currentUser.role === 'superadministrator'
        || currentUser.role === 'administrator'
        || (currentUser.allowed_modules ?? []).some((k: string) => ['m.102', 'm.103', 'm.108', 'm.110', 'm.111'].includes(k));

    const fullModuleCatalog = useMemo(() => orderedAssignmentKeys(BI_MODULE_KEYS), []);

    const landingCards = useMemo(() => landingCardsForUser(currentUser), [currentUser]);
    const visibleThemes = useMemo(() => visibleThemesForUser(currentUser), [currentUser]);
    // Theme state: koja tema je trenutno odabrana na landingu. null = pokaži teme.
    const [activeTheme, setActiveTheme] = useState<AdminThemeId | null>(null);

    // m.170 — obavijesti modula (badge na landing kartici). Osobne (tag/thread) crveno, opće zeleno.
    const [notesUnread, setNotesUnread] = useState(0);
    const [notesPersonal, setNotesPersonal] = useState(0);
    useEffect(() => {
        if (!isSuperadmin) return;
        let alive = true;
        api<{ unread: number; personal?: number }>('/module-help/notes-unread', token)
            .then((r) => { if (alive) { setNotesUnread(r.unread ?? 0); setNotesPersonal(r.personal ?? 0); } })
            .catch(() => { /* tiho */ });
        return () => { alive = false; };
    }, [token, isSuperadmin]);

    function moduleLabelForAssignmentKey(key: string): string {
        if ((BI_MODULE_KEYS as readonly string[]).includes(key)) {
            return moduleTitle(key as ModuleKey, locale);
        }

        return assignmentKeyLabel(key, locale);
    }

    const [users, setUsers] = useState<BiAdminUser[]>([]);
    const [query, setQuery] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
    const toggleUserSelection = (id: number) => {
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const clearUserSelection = () => setSelectedUserIds(new Set());
    const [userScope, setUserScope] = useState<'active' | 'archived' | 'all'>('active');
    const [showPendingOnly, setShowPendingOnly] = useState(false);
    const [userSort, setUserSort] = useState<{ key: UserSortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
    const [userViewMode, setUserViewMode] = useState<'flat' | 'grouped'>('flat');
    const [loading, setLoading] = useState(true);
    const [localError, setLocalError] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [cName, setCName] = useState('');
    const [cEmail, setCEmail] = useState('');
    const [cPass, setCPass] = useState('');
    const [cRole, setCRole] = useState('user');
    const [cActive, setCActive] = useState(true);
    const [cProt, setCProt] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [previewUser, setPreviewUser] = useState<User | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    // Side drawer (m.101 hibrid): klik na red u tablici otvara desni panel s detaljima i akcijama.
    const [drawerUserId, setDrawerUserId] = useState<number | null>(() => {
        if (typeof window === 'undefined') return null;
        const u = new URLSearchParams(window.location.search).get('u');
        return u && /^\d+$/.test(u) ? Number(u) : null;
    });

    // Sync drawerUserId u URL (?u=43) — radi u kombinaciji s ?admin=101 za pune deep linkove
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const url = new URL(window.location.href);
        if (drawerUserId == null) {
            if (url.searchParams.has('u')) {
                url.searchParams.delete('u');
                window.history.replaceState({}, '', url.toString());
            }
        } else if (url.searchParams.get('u') !== String(drawerUserId)) {
            url.searchParams.set('u', String(drawerUserId));
            window.history.replaceState({}, '', url.toString());
        }
    }, [drawerUserId]);

    function copyDeepLink(extraNote?: string) {
        if (typeof window === 'undefined') return;
        const url = window.location.href;
        navigator.clipboard?.writeText(url).then(() => {
            alert((extraNote ?? 'Link kopiran u clipboard:') + '\n' + url);
        }).catch(() => {
            window.prompt('Kopiraj link:', url);
        });
    }
    const [eName, setEName] = useState('');
    const [eEmail, setEEmail] = useState('');
    const [ePass, setEPass] = useState('');
    const [eRole, setERole] = useState('user');
    const [eActive, setEActive] = useState(true);
    const [eProt, setEProt] = useState(false);
    const [eCanCreateCirculars, setECanCreateCirculars] = useState(false);
    const [eCanManageCircularGroups, setECanManageCircularGroups] = useState(false);
    const [assignmentLoading, setAssignmentLoading] = useState(false);
    const [assignmentError, setAssignmentError] = useState('');
    const [assignmentUsers, setAssignmentUsers] = useState<UserAssignmentRow[]>([]);
    const [assignmentCompanies, setAssignmentCompanies] = useState<AssignmentCompany[]>([]);
    const [assignmentSectors, setAssignmentSectors] = useState<AssignmentSector[]>([]);
    const [assignmentDepartments, setAssignmentDepartments] = useState<AssignmentDepartment[]>([]);
    const [assignmentWarehouses, setAssignmentWarehouses] = useState<AssignmentWarehouse[]>([]);
    const [assignmentQuery, setAssignmentQuery] = useState('');
    const [assignmentUserId, setAssignmentUserId] = useState<number | null>(null);
    const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
    const [aRole, setARole] = useState('user');
    const [aCompanyIds, setACompanyIds] = useState<number[]>([]);
    const [aSectorIds, setASectorIds] = useState<number[]>([]);
    const [aDepartmentIds, setADepartmentIds] = useState<number[]>([]);
    const [aPrimaryDepartmentId, setAPrimaryDepartmentId] = useState<number | null>(null);
    // Per-user dodjela skladišta (m.40.3). overrideWarehouses=false → koristi auto-default iz poslovnica.
    const [aWarehouseOverride, setAWarehouseOverride] = useState(false);
    const [aWarehouseCodes, setAWarehouseCodes] = useState<string[]>([]);
    const [aDefaultWarehouseCodes, setADefaultWarehouseCodes] = useState<string[]>([]);
    const [aWarehouseIsAdmin, setAWarehouseIsAdmin] = useState(false);
    const [aStorageSupplierAccess, setAStorageSupplierAccess] = useState(false);
    const [narrowUserModules, setNarrowUserModules] = useState(false);
    const [aModuleKeys, setAModuleKeys] = useState<string[]>([]);
    const [companyModuleDraft, setCompanyModuleDraft] = useState<Record<number, string[]>>({});
    const [assignmentSaving, setAssignmentSaving] = useState(false);
    const [assignmentSuccess, setAssignmentSuccess] = useState('');
    const [structureLoading, setStructureLoading] = useState(false);
    const [structureError, setStructureError] = useState('');
    const [structureSuccess, setStructureSuccess] = useState('');
    const [structure, setStructure] = useState<StructurePayload | null>(null);
    const [sectorCompanyId, setSectorCompanyId] = useState('');
    const [sectorCode, setSectorCode] = useState('');
    const [sectorNameHr, setSectorNameHr] = useState('');
    const [sectorNameEn, setSectorNameEn] = useState('');
    const [productNameHr, setProductNameHr] = useState('');
    const [productSlug, setProductSlug] = useState('');
    const [productSku, setProductSku] = useState('');
    const [productFineWeightG, setProductFineWeightG] = useState('');
    const [productLengthMm, setProductLengthMm] = useState('');
    const [productWidthMm, setProductWidthMm] = useState('');
    const [productThicknessMm, setProductThicknessMm] = useState('');
    const [productDiameterMm, setProductDiameterMm] = useState('');
    const [productImageFile, setProductImageFile] = useState<File | null>(null);
    const [productSalePrice, setProductSalePrice] = useState('');
    const [productBuyPrice, setProductBuyPrice] = useState('');
    const [productPremiumPoslovnica, setProductPremiumPoslovnica] = useState('');
    const [productPremiumSlovenija, setProductPremiumSlovenija] = useState('');
    const [productPremiumBugarska, setProductPremiumBugarska] = useState('');
    const [productCategory, setProductCategory] = useState('');
    const [productSupplier, setProductSupplier] = useState('');
    const [productCompanyIds, setProductCompanyIds] = useState<number[]>([]);
    const [productSectorIds, setProductSectorIds] = useState<number[]>([]);
    const [editingSectorId, setEditingSectorId] = useState<number | null>(null);
    const [departmentDrawerMode, setDepartmentDrawerMode] = useState<'create' | 'edit' | null>(null);
    const [departmentDrawerId, setDepartmentDrawerId] = useState<number | null>(null);
    const [departmentDrawerCompanyId, setDepartmentDrawerCompanyId] = useState('');
    const [departmentDrawerSectorId, setDepartmentDrawerSectorId] = useState('');
    const [departmentDrawerCode, setDepartmentDrawerCode] = useState('');
    const [departmentDrawerName, setDepartmentDrawerName] = useState('');
    const [departmentDrawerRegion, setDepartmentDrawerRegion] = useState('');
    const [departmentDrawerCity, setDepartmentDrawerCity] = useState('');
    const [departmentDrawerBusy, setDepartmentDrawerBusy] = useState(false);
    const [editingProductId, setEditingProductId] = useState<number | null>(null);
    const [editSectorCode, setEditSectorCode] = useState('');
    const [editSectorNameHr, setEditSectorNameHr] = useState('');
    const [editSectorNameEn, setEditSectorNameEn] = useState('');
    const [editProductNameHr, setEditProductNameHr] = useState('');
    const [editProductSlug, setEditProductSlug] = useState('');
    const [editProductSku, setEditProductSku] = useState('');
    const [editProductFineWeightG, setEditProductFineWeightG] = useState('');
    const [editProductPurity, setEditProductPurity] = useState('');
    const [editProductLengthMm, setEditProductLengthMm] = useState('');
    const [editProductWidthMm, setEditProductWidthMm] = useState('');
    const [editProductThicknessMm, setEditProductThicknessMm] = useState('');
    const [editProductDiameterMm, setEditProductDiameterMm] = useState('');
    const [editProductImageFile, setEditProductImageFile] = useState<File | null>(null);
    const [editProductPremiumPoslovnica, setEditProductPremiumPoslovnica] = useState('');
    const [editProductPremiumSlovenija, setEditProductPremiumSlovenija] = useState('');
    const [editProductPremiumBugarska, setEditProductPremiumBugarska] = useState('');
    const [editProductSalePrice, setEditProductSalePrice] = useState('');
    const [editProductBuyPrice, setEditProductBuyPrice] = useState('');
    const [editProductIsActive, setEditProductIsActive] = useState<boolean>(true);
    // cgm_category text + sale/buy price uklonjeno — koristimo samo category_id (FK)
    const [editProductSupplier, setEditProductSupplier] = useState('');
    const [editProductCompanyIds, setEditProductCompanyIds] = useState<number[]>([]);
    const [editProductSectorIds, setEditProductSectorIds] = useState<number[]>([]);
    // 110 nova polja
    const [editProductCategoryId, setEditProductCategoryId] = useState<number | ''>('');
    const [editProductMintPartnerId, setEditProductMintPartnerId] = useState<number | ''>('');
    const [editProductDeliveryTypeId, setEditProductDeliveryTypeId] = useState<number | ''>('');
    const [editProductTypeId, setEditProductTypeId] = useState<number | ''>('');
    const [editProductKpdSifra, setEditProductKpdSifra] = useState('');
    const [editProductKpdNaziv, setEditProductKpdNaziv] = useState('');
    const [editProductStopaPdv, setEditProductStopaPdv] = useState('');
    // 110 sorting + filtri
    const [productSortKey, setProductSortKey] = useState<'id' | 'sku' | 'name_hr' | 'kpd_sifra' | 'stopa_pdv' | 'category' | 'mint' | 'type'>('id');
    const [productSortDir, setProductSortDir] = useState<'asc' | 'desc'>('asc');
    const [productFilterCategoryIds, setProductFilterCategoryIds] = useState<number[]>([]);
    const [productFilterTypeIds, setProductFilterTypeIds] = useState<number[]>([]);
    const [productFilterMintIds, setProductFilterMintIds] = useState<number[]>([]);
    const [productShowArchived, setProductShowArchived] = useState(false);
    const [productShowInactive, setProductShowInactive] = useState(false);
    // 111 Partneri
    const [partnerName, setPartnerName] = useState('');
    const [partnerRole, setPartnerRole] = useState<'mint' | 'supplier' | 'both' | 'other'>('supplier');
    const [partnerCountry, setPartnerCountry] = useState('');
    const [partnerEmail, setPartnerEmail] = useState('');
    const [partnerPhone, setPartnerPhone] = useState('');
    const [partnerNotes, setPartnerNotes] = useState('');
    const [editingPartnerId, setEditingPartnerId] = useState<number | null>(null);
    const [editPartnerName, setEditPartnerName] = useState('');
    const [editPartnerRole, setEditPartnerRole] = useState<'mint' | 'supplier' | 'both' | 'other'>('supplier');
    const [editPartnerCountry, setEditPartnerCountry] = useState('');
    const [editPartnerEmail, setEditPartnerEmail] = useState('');
    const [editPartnerPhone, setEditPartnerPhone] = useState('');
    const [editPartnerNotes, setEditPartnerNotes] = useState('');
    // m.111 filteri: quicksearch + uloga + država
    const [partnerQuery, setPartnerQuery] = useState('');
    const [partnerRoleFilter, setPartnerRoleFilter] = useState<'all' | 'mint' | 'supplier' | 'both' | 'other'>('all');
    const [partnerCountryFilter, setPartnerCountryFilter] = useState('all');
    // 110.1/110.2/110.3 lookups
    const [lookupName, setLookupName] = useState<Record<string, string>>({ types: '', delivery_types: '', categories: '' });
    const [lookupDaysMin, setLookupDaysMin] = useState('');
    const [lookupDaysMax, setLookupDaysMax] = useState('');
    const [editingLookup, setEditingLookup] = useState<{ kind: string; id: number; name: string; days_min?: string; days_max?: string } | null>(null);
    const [correctionsLoading, setCorrectionsLoading] = useState(false);
    const [correctionsError, setCorrectionsError] = useState('');
    const [correctionsRows, setCorrectionsRows] = useState<LoanCorrectionRow[]>([]);
    const [correctionCompanies, setCorrectionCompanies] = useState<Array<{ id: number; code: string; name: string }>>([]);
    const [correctionParties, setCorrectionParties] = useState<Array<{ id: number; name: string }>>([]);
    const [newCorrectionCompanyId, setNewCorrectionCompanyId] = useState('');
    const [newCorrectionCounterpartyId, setNewCorrectionCounterpartyId] = useState('');
    const [newCorrectionDate, setNewCorrectionDate] = useState('');
    const [newCorrectionAmount, setNewCorrectionAmount] = useState('');
    const [newCorrectionNote, setNewCorrectionNote] = useState('');
    const [editingCorrectionId, setEditingCorrectionId] = useState<number | null>(null);
    const [editCorrectionCompanyId, setEditCorrectionCompanyId] = useState('');
    const [editCorrectionCounterpartyId, setEditCorrectionCounterpartyId] = useState('');
    const [editCorrectionDate, setEditCorrectionDate] = useState('');
    const [editCorrectionAmount, setEditCorrectionAmount] = useState('');
    const [editCorrectionNote, setEditCorrectionNote] = useState('');
    const [sectorStructureQuery, setSectorStructureQuery] = useState('');
    const [sectorCompanyFilter, setSectorCompanyFilter] = useState<Array<number | string>>([]);
    const [departmentStructureQuery, setDepartmentStructureQuery] = useState('');
    const [departmentCompanyFilter, setDepartmentCompanyFilter] = useState<Array<number | string>>([]);
    const [productStructureQuery, setProductStructureQuery] = useState('');
    const [correctionSearchQuery, setCorrectionSearchQuery] = useState('');
    const [adminSection, setAdminSection] = useState<AdminSectionId>(() =>
        typeof window !== 'undefined' ? parseAdminSectionFromSearch(window.location.search) : 'landing',
    );
    const [invoiceReloadTick, setInvoiceReloadTick] = useState(0);
    const [finReloadTick, setFinReloadTick] = useState(0);
    const [purchaseOrderReloadTick, setPurchaseOrderReloadTick] = useState(0);
    const [e2RegistryReloadTick, setE2RegistryReloadTick] = useState(0);

    const loadUsers = useCallback(async () => {
        if (!isSuperadmin) {
            setUsers([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setLocalError('');
        try {
            const res = await api<{ users: BiAdminUser[] }>(`/admin/users?scope=${userScope}`, token);
            setUsers(res.users);
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'Greška');
        } finally {
            setLoading(false);
        }
    }, [token, isSuperadmin, userScope]);

    useEffect(() => {
        void loadUsers();
    }, [loadUsers]);

    const editTarget = useMemo(() => users.find((u) => u.id === editId), [users, editId]);
    const isProtectedTarget = Boolean(editTarget?.is_protected);
    const filteredUsers = useMemo(
        () => users
            .filter((u) => !showPendingOnly || u.status === 'pending')
            .filter((u) => matchesQuickSearch([u.id, u.name, u.email, u.role, u.status, u.primary_department_label, u.is_active ? 'aktivan da' : 'aktivan ne', u.is_protected ? 'zaštićen da' : 'zaštićen ne'], query)),
        [users, query, showPendingOnly],
    );
    const pendingUsersCount = useMemo(() => users.filter((u) => u.status === 'pending').length, [users]);
    const sortedUsers = useMemo(() => {
        const accessor = (u: BiAdminUser): string | number => {
            switch (userSort.key) {
                case 'name': return u.name.toLowerCase();
                case 'email': return (u.email ?? '').toLowerCase();
                case 'company': return (u.company_label ?? '').toLowerCase();
                case 'department': return (u.primary_department_label ?? '').toLowerCase();
                case 'home_wh': return (u.primary_department_label ?? '').toLowerCase();
                case 'modules': return u.modules_count ?? 0;
                case 'role': return u.role;
                case 'status': return u.status ?? '';
                case 'activity': return u.last_activity_at ?? '';
                case 'protected': return u.is_protected ? 1 : 0;
            }
        };
        return [...filteredUsers].sort((a, b) => {
            const av = accessor(a);
            const bv = accessor(b);
            if (av < bv) return userSort.dir === 'asc' ? -1 : 1;
            if (av > bv) return userSort.dir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredUsers, userSort]);
    const sortTh = (key: UserSortKey, label: string) => (
        <th
            className="bi-sort-th"
            onClick={() => setUserSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}
        >
            {label}
            {userSort.key === key ? <span className="bi-sort-arrow"> {userSort.dir === 'asc' ? '▲' : '▼'}</span> : null}
        </th>
    );

    // Klasifikacija per korisnik za grouped view (isto kao u directoryModule)
    function classifyUserRow(u: BiAdminUser): 'uprava' | 'poslovnice' | 'ostalo' {
        const d = (u.primary_department_label ?? '').trim().toUpperCase();
        if (d.startsWith('POSL')) return 'poslovnice';
        if (/^\d{3,5}/.test(d)) return 'uprava';
        return 'ostalo';
    }
    const groupedUsers = useMemo(() => {
        const out: Record<'uprava' | 'poslovnice' | 'ostalo', Record<string, BiAdminUser[]>> = {
            uprava: {}, poslovnice: {}, ostalo: {},
        };
        for (const u of sortedUsers) {
            const cat = classifyUserRow(u);
            // Poslovnice se grupiraju po regiji (ili "Bez regije" ako nije postavljena);
            // Uprava i Ostalo i dalje po tvrtki.
            const groupKey = cat === 'poslovnice'
                ? (u.primary_department_region ?? (locale === 'en' ? 'No region' : 'Bez regije'))
                : (u.company_label ?? '—');
            if (!out[cat][groupKey]) out[cat][groupKey] = [];
            out[cat][groupKey].push(u);
        }
        // Unutar Poslovnica: sort users po city → branch_name → ime.
        const cmpLoc = (a: string, b: string) => a.localeCompare(b, locale === 'en' ? 'en' : 'hr', { sensitivity: 'base' });
        for (const region of Object.keys(out.poslovnice)) {
            out.poslovnice[region].sort((a, b) => {
                const cityA = a.primary_department_city ?? '￿';
                const cityB = b.primary_department_city ?? '￿';
                const byCity = cmpLoc(cityA, cityB);
                if (byCity !== 0) return byCity;
                const byBranch = cmpLoc(a.primary_department_label ?? '', b.primary_department_label ?? '');
                if (byBranch !== 0) return byBranch;
                return cmpLoc(a.name ?? '', b.name ?? '');
            });
        }
        return out;
    }, [sortedUsers, locale]);
    const userCategoryMeta: Array<{ key: 'uprava' | 'poslovnice' | 'ostalo'; labelHr: string; labelEn: string; icon: string; color: string }> = [
        { key: 'uprava', labelHr: 'Uprava', labelEn: 'Administration', icon: '🏛', color: 'var(--ad-ink)' },
        { key: 'poslovnice', labelHr: 'Poslovnice', labelEn: 'Branches', icon: '🏪', color: '#1f7a36' },
        { key: 'ostalo', labelHr: 'Ostalo', labelEn: 'Other', icon: '👥', color: 'var(--ad-acc-3)' },
    ];
    const selectedAssignmentUser = useMemo(
        () => assignmentUsers.find((user) => user.id === assignmentUserId) ?? null,
        [assignmentUsers, assignmentUserId],
    );
    const filteredAssignmentUsers = useMemo(
        () => assignmentUsers.filter((u) => matchesQuickSearch([u.name, u.email, u.role], assignmentQuery)),
        [assignmentUsers, assignmentQuery],
    );
    const adminBreadcrumbTitle = useMemo(() => {
        if (adminSection === 'landing') {
            return '';
        }
        const def = adminSectionDefinition(adminSection);

        return def ? adminSectionNavLabel(def, locale) : '';
    }, [adminSection, locale]);
    const availableSectors = useMemo(
        () => assignmentSectors.filter((sector) => aCompanyIds.includes(sector.company_id)),
        [assignmentSectors, aCompanyIds],
    );
    const sectorById = useMemo(
        () => new Map(assignmentSectors.map((sector) => [sector.id, sector])),
        [assignmentSectors],
    );
    const companyById = useMemo(
        () => new Map(assignmentCompanies.map((company) => [company.id, company])),
        [assignmentCompanies],
    );
    const availableDepartments = useMemo(
        () => assignmentDepartments.filter((department) => {
            if (!aCompanyIds.includes(department.company_id)) {
                return false;
            }
            return true;
        }).sort((left, right) => {
            const leftCompany = companyById.get(left.company_id)?.code ?? companyById.get(left.company_id)?.name ?? '';
            const rightCompany = companyById.get(right.company_id)?.code ?? companyById.get(right.company_id)?.name ?? '';
            const byCompany = leftCompany.localeCompare(rightCompany);
            if (byCompany !== 0) {
                return byCompany;
            }
            const leftSector = left.sector_id ? (sectorById.get(left.sector_id)?.code ?? sectorById.get(left.sector_id)?.name_hr ?? '') : '';
            const rightSector = right.sector_id ? (sectorById.get(right.sector_id)?.code ?? sectorById.get(right.sector_id)?.name_hr ?? '') : '';
            const bySector = leftSector.localeCompare(rightSector);
            if (bySector !== 0) {
                return bySector;
            }
            return (left.name ?? '').localeCompare(right.name ?? '');
        }),
        [assignmentDepartments, aCompanyIds, companyById, sectorById],
    );
    const availableProductSectors = useMemo(() => {
        if (!structure) {
            return [];
        }
        return structure.sectors.filter((sector) => productCompanyIds.includes(sector.company_id));
    }, [structure, productCompanyIds]);
    const availableEditProductSectors = useMemo(() => {
        if (!structure) {
            return [];
        }
        return structure.sectors.filter((sector) => editProductCompanyIds.includes(sector.company_id));
    }, [structure, editProductCompanyIds]);
    const availableDepartmentSectors = useMemo(() => {
        if (!structure || !departmentDrawerCompanyId) {
            return [];
        }
        return structure.sectors.filter((sector) => Number(sector.company_id) === Number(departmentDrawerCompanyId));
    }, [structure, departmentDrawerCompanyId]);

    const filteredStructureSectors = useMemo(() => {
        if (!structure) {
            return [];
        }
        const companyAllow = new Set(sectorCompanyFilter.map((v) => Number(v)));
        return structure.sectors.filter((row) => {
            if (companyAllow.size > 0 && !companyAllow.has(Number(row.company_id))) {
                return false;
            }
            return matchesQuickSearch(
                [
                    row.id,
                    row.code,
                    row.name_hr,
                    row.name_en,
                    structure.companies.find((c) => Number(c.id) === Number(row.company_id))?.code,
                    structure.companies.find((c) => Number(c.id) === Number(row.company_id))?.name,
                ],
                sectorStructureQuery,
            );
        });
    }, [structure, sectorStructureQuery, sectorCompanyFilter]);

    const filteredStructureDepartments = useMemo(() => {
        if (!structure) {
            return [];
        }
        const companyAllow = new Set(departmentCompanyFilter.map((v) => Number(v)));
        const cmpLoc = (a: string, b: string) => a.localeCompare(b, locale === 'en' ? 'en' : 'hr', { sensitivity: 'base' });
        const noRegionLabels = ['Bez regije', 'No region', ''];
        return structure.departments
            .filter((row) => {
                if (companyAllow.size > 0 && !companyAllow.has(Number(row.company_id))) {
                    return false;
                }
                return matchesQuickSearch(
                    [
                        row.id,
                        row.code,
                        row.name,
                        row.region,
                        row.city,
                        structure.companies.find((c) => Number(c.id) === Number(row.company_id))?.code,
                        structure.companies.find((c) => Number(c.id) === Number(row.company_id))?.name,
                        structure.sectors.find((s) => Number(s.id) === Number(row.sector_id))?.code,
                    ],
                    departmentStructureQuery,
                );
            })
            .slice()
            .sort((a, b) => {
                // Sort: regija (bez regije na kraj) → grad → naziv poslovnice
                const regionA = a.region ?? '';
                const regionB = b.region ?? '';
                const aNoReg = noRegionLabels.includes(regionA);
                const bNoReg = noRegionLabels.includes(regionB);
                if (aNoReg !== bNoReg) return aNoReg ? 1 : -1;
                const byRegion = cmpLoc(regionA, regionB);
                if (byRegion !== 0) return byRegion;
                const byCity = cmpLoc(a.city ?? '￿', b.city ?? '￿');
                if (byCity !== 0) return byCity;
                return cmpLoc(a.name ?? '', b.name ?? '');
            });
    }, [structure, departmentStructureQuery, departmentCompanyFilter, locale]);

    const filteredStructureProducts = useMemo(() => {
        if (!structure) return [];
        let list = structure.products.filter((row) =>
            matchesQuickSearch(
                [row.id, row.name_hr, row.slug, row.sku ?? '', row.current_sale_price_eur, row.current_buy_price_eur, (row as any).kpd_sifra ?? ''],
                productStructureQuery,
            ),
        );
        if (! productShowArchived) {
            list = list.filter((r) => ! (r as any).deleted_at);
        }
        if (! productShowInactive) {
            list = list.filter((r) => {
                const v = (r as any).is_active;
                return v !== 0 && v !== '0' && v !== false;
            });
        }
        if (productFilterCategoryIds.length > 0) {
            list = list.filter((r) => (r as any).category_id && productFilterCategoryIds.includes(Number((r as any).category_id)));
        }
        if (productFilterTypeIds.length > 0) {
            list = list.filter((r) => (r as any).product_type_id && productFilterTypeIds.includes(Number((r as any).product_type_id)));
        }
        if (productFilterMintIds.length > 0) {
            list = list.filter((r) => (r as any).mint_partner_id && productFilterMintIds.includes(Number((r as any).mint_partner_id)));
        }
        // Sort
        const dir = productSortDir === 'asc' ? 1 : -1;
        list = [...list].sort((a, b) => {
            const av = productSortKey === 'category' ? (structure.product_categories_lookup ?? []).find((c) => Number(c.id) === Number((a as any).category_id))?.name ?? ''
                     : productSortKey === 'mint' ? (structure.partners ?? []).find((p) => Number(p.id) === Number((a as any).mint_partner_id))?.name ?? ''
                     : productSortKey === 'type' ? (structure.product_types ?? []).find((t) => Number(t.id) === Number((a as any).product_type_id))?.name ?? ''
                     : productSortKey === 'name_hr' ? a.name_hr ?? ''
                     : productSortKey === 'sku' ? (a.sku ?? '')
                     : productSortKey === 'kpd_sifra' ? ((a as any).kpd_sifra ?? '')
                     : productSortKey === 'stopa_pdv' ? ((a as any).stopa_pdv ?? 0)
                     : a.id;
            const bv = productSortKey === 'category' ? (structure.product_categories_lookup ?? []).find((c) => Number(c.id) === Number((b as any).category_id))?.name ?? ''
                     : productSortKey === 'mint' ? (structure.partners ?? []).find((p) => Number(p.id) === Number((b as any).mint_partner_id))?.name ?? ''
                     : productSortKey === 'type' ? (structure.product_types ?? []).find((t) => Number(t.id) === Number((b as any).product_type_id))?.name ?? ''
                     : productSortKey === 'name_hr' ? b.name_hr ?? ''
                     : productSortKey === 'sku' ? (b.sku ?? '')
                     : productSortKey === 'kpd_sifra' ? ((b as any).kpd_sifra ?? '')
                     : productSortKey === 'stopa_pdv' ? ((b as any).stopa_pdv ?? 0)
                     : b.id;
            if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
            // Numeric-aware: ako su oba string parsivi u broj, sortiraj brojčano (1 < 10 < 100, ne 1 < 10 < 11 < 100 kao string)
            const aStr = String(av);
            const bStr = String(bv);
            const aNum = Number(aStr);
            const bNum = Number(bStr);
            if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && aStr.trim() !== '' && bStr.trim() !== '') {
                return (aNum - bNum) * dir;
            }
            return aStr.localeCompare(bStr, undefined, { numeric: true }) * dir;
        });
        return list;
    }, [structure, productStructureQuery, productShowArchived, productShowInactive, productFilterCategoryIds, productFilterTypeIds, productFilterMintIds, productSortKey, productSortDir]);

    const usedMintIds = useMemo(() => {
        if (!structure) return [];
        return Array.from(new Set(structure.products.filter((p) => !(p as any).deleted_at && (p as any).mint_partner_id).map((p) => Number((p as any).mint_partner_id))));
    }, [structure]);
    const usedCategoryIds = useMemo(() => {
        if (!structure) return [];
        return Array.from(new Set(structure.products.filter((p) => !(p as any).deleted_at && (p as any).category_id).map((p) => Number((p as any).category_id))));
    }, [structure]);
    const usedTypeIds = useMemo(() => {
        if (!structure) return [];
        return Array.from(new Set(structure.products.filter((p) => !(p as any).deleted_at && (p as any).product_type_id).map((p) => Number((p as any).product_type_id))));
    }, [structure]);

    function toggleSort(key: typeof productSortKey) {
        if (productSortKey === key) setProductSortDir(productSortDir === 'asc' ? 'desc' : 'asc');
        else { setProductSortKey(key); setProductSortDir('asc'); }
    }

    const filteredCorrectionRows = useMemo(
        () =>
            correctionsRows.filter((row) =>
                matchesQuickSearch(
                    [
                        row.id,
                        row.company_code,
                        row.counterparty_name,
                        row.effective_date,
                        row.amount,
                        row.status,
                        row.note ?? '',
                    ],
                    correctionSearchQuery,
                ),
            ),
        [correctionsRows, correctionSearchQuery],
    );

    const loadAssignments = useCallback(async () => {
        if (!isSuperadmin) {
            setAssignmentUsers([]);
            return;
        }
        setAssignmentLoading(true);
        setAssignmentError('');
        try {
            const payload = await api<{
                users?: Array<Record<string, unknown>>;
                companies?: Array<Record<string, unknown>>;
                sectors?: Array<Record<string, unknown>>;
                departments?: Array<Record<string, unknown>>;
            }>('/admin/assignments', token);

            const companies = (payload.companies ?? []).map((company) => ({
                id: Number(company.id),
                code: typeof company.code === 'string' ? company.code : undefined,
                name: typeof company.name === 'string' ? company.name : `Company #${company.id}`,
                enabled_modules: Array.isArray(company.enabled_modules)
                    ? (company.enabled_modules as string[])
                    : company.enabled_modules === null
                        ? null
                        : undefined,
            }));
            const sectors = (payload.sectors ?? []).map((sector) => ({
                id: Number(sector.id),
                company_id: Number(sector.company_id),
                code: typeof sector.code === 'string' ? sector.code : undefined,
                name_hr: typeof sector.name_hr === 'string' ? sector.name_hr : `Sector #${sector.id}`,
            }));
            const departments = (payload.departments ?? []).map((department) => ({
                id: Number(department.id),
                company_id: Number(department.company_id),
                sector_id: department.sector_id == null ? null : Number(department.sector_id),
                code: typeof department.code === 'string' ? department.code : undefined,
                name: typeof department.name === 'string' ? department.name : `Department #${department.id}`,
            }));
            const usersForAssignments: UserAssignmentRow[] = (payload.users ?? []).map((user) => {
                const assignment = typeof user.assignment === 'object' && user.assignment !== null ? user.assignment as Record<string, unknown> : {};
                const catalogRaw = user.company_module_catalog;
                const allowedKeys = new Set(fullModuleCatalog);
                const catalog = Array.isArray(catalogRaw)
                    ? catalogRaw.map((key) => String(key)).filter((key) => allowedKeys.has(key))
                    : [];
                const pdIdRaw = (user as any).primary_department_id ?? assignment.primary_department_id;
                const pdId = pdIdRaw != null && pdIdRaw !== '' && Number.isFinite(Number(pdIdRaw)) ? Number(pdIdRaw) : null;
                return {
                    id: Number(user.id),
                    name: typeof user.name === 'string' ? user.name : `User #${user.id}`,
                    email: typeof user.email === 'string' ? user.email : '',
                    role: typeof user.role === 'string' ? user.role : 'user',
                    company_ids: normalizeNumberArray(user.company_ids ?? assignment.company_ids),
                    sector_ids: normalizeNumberArray(user.sector_ids ?? assignment.sector_ids),
                    department_ids: normalizeNumberArray(user.department_ids ?? assignment.department_ids),
                    primary_department_id: pdId,
                    enabled_modules: Array.isArray(user.enabled_modules)
                        ? (user.enabled_modules as string[])
                        : user.enabled_modules === null
                            ? null
                            : undefined,
                    company_module_catalog: catalog.length > 0 ? catalog : [...fullModuleCatalog],
                    warehouse_codes: Array.isArray((user as any).warehouse_codes) ? (user as any).warehouse_codes.map(String) : [],
                    default_warehouse_codes: Array.isArray((user as any).default_warehouse_codes) ? (user as any).default_warehouse_codes.map(String) : [],
                    warehouse_is_admin: Boolean((user as any).warehouse_is_admin),
                } as UserAssignmentRow;
            });

            setAssignmentWarehouses(Array.isArray((payload as any).warehouses) ? (payload as any).warehouses.map((w: any) => ({ code: String(w.code), name: String(w.name) })) : []);
            setAssignmentCompanies(companies.filter((row) => Number.isFinite(row.id)));
            setAssignmentSectors(sectors.filter((row) => Number.isFinite(row.id) && Number.isFinite(row.company_id)));
            setAssignmentDepartments(departments.filter((row) => Number.isFinite(row.id) && Number.isFinite(row.company_id)));
            setAssignmentUsers(usersForAssignments.filter((row) => Number.isFinite(row.id)));
            setAssignmentUserId((current) => current ?? usersForAssignments[0]?.id ?? null);
        } catch (err) {
            setAssignmentError(err instanceof Error ? err.message : 'Greška pri učitavanju dodjela.');
        } finally {
            setAssignmentLoading(false);
        }
    }, [token, isSuperadmin, fullModuleCatalog]);

    const loadStructure = useCallback(async () => {
        if (!canManageStructure) {
            setStructure(null);
            return;
        }
        setStructureLoading(true);
        setStructureError('');
        try {
            const payload = await api<StructurePayload>('/admin/structure', token);
            setStructure(payload);
            setSectorCompanyId((current) => current || String(payload.companies[0]?.id ?? ''));
        } catch (err) {
            setStructureError(err instanceof Error ? err.message : 'Greška pri učitavanju strukture.');
        } finally {
            setStructureLoading(false);
        }
    }, [token, canManageStructure]);

    const loadCorrections = useCallback(async () => {
        if (!isSuperadmin) {
            setCorrectionsRows([]);
            return;
        }
        setCorrectionsLoading(true);
        setCorrectionsError('');
        try {
            const payload = await api<LoanCorrectionPayload>('/admin/loan-corrections', token);
            setCorrectionsRows(payload.rows);
            setCorrectionCompanies(payload.companies);
            setCorrectionParties(payload.counterparties);
            setNewCorrectionCompanyId((current) => current || String(payload.companies[0]?.id ?? ''));
        } catch (err) {
            setCorrectionsError(err instanceof Error ? err.message : 'Greška pri učitavanju korekcija.');
        } finally {
            setCorrectionsLoading(false);
        }
    }, [token, isSuperadmin]);

    const goLanding = useCallback(() => {
        setAdminSection('landing');
        setActiveTheme(null);
        setShowCreate(false);
        setEditId(null);
        setEditingSectorId(null);
        setDepartmentDrawerMode(null);
        setDepartmentDrawerId(null);
        setEditingProductId(null);
        setEditingCorrectionId(null);
    }, []);

    const refreshCurrentSection = useCallback(() => {
        switch (adminSection) {
            case '101':
                void loadUsers();
                return;
            case '104':
                void loadAssignments();
                return;
            case '108':
            case '102':
            case '103':
            case '110':
                void loadStructure();
                return;
            case '1211':
            case '1212':
            case '1213':
                setInvoiceReloadTick((n) => n + 1);
                return;
            case '122':
            case '123':
            case '124':
                setFinReloadTick((n) => n + 1);
                return;
            case '125':
                setPurchaseOrderReloadTick((n) => n + 1);
                return;
            case '126':
            case '127':
                setE2RegistryReloadTick((n) => n + 1);
                return;
            default:
                return;
        }
    }, [adminSection, loadUsers, loadAssignments, loadCorrections, loadStructure]);

    useEffect(() => {
        syncAdminSectionUrl(adminSection);
    }, [adminSection]);

    useEffect(() => {
        if (adminSection === 'landing') {
            return;
        }
        if (!userMayAccessAdminSection(currentUser, adminSection)) {
            goLanding();
        }
    }, [adminSection, currentUser, goLanding]);

    useEffect(() => {
        void loadAssignments();
    }, [loadAssignments]);

    // Kad user navigira između admin sekcija, refresh ciljanu sekciju da pokupi novi
    // stvoreni entitet bez manualnog refresha (npr. user kreiran u 101 → vidljiv u 104).
    useEffect(() => {
        if (adminSection === 'landing') return;
        refreshCurrentSection();
    }, [adminSection, refreshCurrentSection]);

    useEffect(() => {
        void loadStructure();
    }, [loadStructure]);

    useEffect(() => {
        void loadCorrections();
    }, [loadCorrections]);

    useEffect(() => {
        if (!selectedAssignmentUser) {
            setARole('user');
            setACompanyIds([]);
            setASectorIds([]);
            setADepartmentIds([]);
            setAPrimaryDepartmentId(null);
            setAWarehouseOverride(false);
            setAWarehouseCodes([]);
            setADefaultWarehouseCodes([]);
            setAWarehouseIsAdmin(false);
            setAStorageSupplierAccess(false);
            setNarrowUserModules(false);
            setAModuleKeys([]);
            return;
        }
        setARole(selectedAssignmentUser.role);
        setACompanyIds(selectedAssignmentUser.company_ids);
        setASectorIds(selectedAssignmentUser.sector_ids);
        setADepartmentIds(selectedAssignmentUser.department_ids);
        setAPrimaryDepartmentId((selectedAssignmentUser as any).primary_department_id ?? null);
        const explicitWh = selectedAssignmentUser.warehouse_codes ?? [];
        setAWarehouseOverride(explicitWh.length > 0);
        setAWarehouseCodes(explicitWh.length > 0 ? explicitWh : (selectedAssignmentUser.default_warehouse_codes ?? []));
        setADefaultWarehouseCodes(selectedAssignmentUser.default_warehouse_codes ?? []);
        setAWarehouseIsAdmin(Boolean(selectedAssignmentUser.warehouse_is_admin));
        setAStorageSupplierAccess(Boolean((selectedAssignmentUser as any).storage_supplier_access));
        const catalog = selectedAssignmentUser.company_module_catalog.length > 0
            ? selectedAssignmentUser.company_module_catalog
            : [...fullModuleCatalog];
        const hasUserOverride = selectedAssignmentUser.enabled_modules != null;
        setNarrowUserModules(hasUserOverride);
        setAModuleKeys(hasUserOverride ? (selectedAssignmentUser.enabled_modules ?? []) : catalog);
    }, [selectedAssignmentUser, fullModuleCatalog]);

    useEffect(() => {
        if (!structure) {
            setCompanyModuleDraft({});
            return;
        }
        const next: Record<number, string[]> = {};
        for (const c of structure.companies) {
            const mod = c.enabled_modules;
            next[c.id] = mod != null && mod.length > 0 ? (mod as string[]) : [...fullModuleCatalog];
        }
        setCompanyModuleDraft(next);
    }, [structure, fullModuleCatalog]);

    useEffect(() => {
        setASectorIds((current) => current.filter((sectorId) => availableSectors.some((sector) => sector.id === sectorId)));
    }, [availableSectors]);

    useEffect(() => {
        setADepartmentIds((current) => current.filter((departmentId) => availableDepartments.some((department) => department.id === departmentId)));
    }, [availableDepartments]);
    useEffect(() => {
        const departmentSectorIds = assignmentDepartments
            .filter((department) => aDepartmentIds.includes(department.id))
            .map((department) => department.sector_id)
            .filter((sectorId): sectorId is number => sectorId != null);
        if (departmentSectorIds.length === 0) {
            return;
        }
        setASectorIds((current) => Array.from(new Set([...current, ...departmentSectorIds])));
    }, [aDepartmentIds, assignmentDepartments]);

    useEffect(() => {
        setProductSectorIds((current) => current.filter((sectorId) => availableProductSectors.some((sector) => sector.id === sectorId)));
    }, [availableProductSectors]);

    function startEdit(u: BiAdminUser) {
        setEditId(u.id);
        setEName(u.name);
        setEEmail(u.email ?? '');
        setEPass('');
        setERole(u.role);
        setEActive(u.is_active);
        setEProt(u.is_protected);
        setECanCreateCirculars(Boolean(u.can_create_circulars));
        setECanManageCircularGroups(Boolean(u.can_manage_circular_groups));
        setLocalError('');
    }

    async function setUserStatus(id: number, status: 'pending' | 'active' | 'inactive') {
        setLocalError('');
        try {
            await api<{ user: BiAdminUser }>(`/admin/users/${id}`, token, {
                method: 'PATCH',
                body: JSON.stringify({ status }),
            });
            await loadUsers();
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function toggleDirectory(u: BiAdminUser) {
        setLocalError('');
        try {
            await api<{ user: BiAdminUser }>(`/admin/users/${u.id}`, token, {
                method: 'PATCH',
                body: JSON.stringify({ show_in_directory: !u.show_in_directory }),
            });
            await loadUsers();
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function openProfilePreview(id: number) {
        setLocalError('');
        setPreviewLoading(true);
        try {
            const r = await api<{ user: User }>(`/admin/users/${id}/profile-preview`, token);
            setPreviewUser(r.user);
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'Greška');
        } finally {
            setPreviewLoading(false);
        }
    }

    function cancelEdit() {
        setEditId(null);
        setEPass('');
    }

    async function submitCreate(event: FormEvent) {
        event.preventDefault();
        setLocalError('');
        try {
            const body = {
                name: cName,
                email: cEmail,
                password: cPass,
                role: cRole,
                is_active: cActive,
                is_protected: cProt && cRole === 'superadministrator',
            };
            await api<{ user: BiAdminUser }>('/admin/users', token, { method: 'POST', body: JSON.stringify(body) });
            setCName('');
            setCEmail('');
            setCPass('');
            setCRole('user');
            setCActive(true);
            setCProt(false);
            setShowCreate(false);
            await loadUsers();
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function submitEdit(event: FormEvent) {
        event.preventDefault();
        if (editId == null) {
            return;
        }
        setLocalError('');
        try {
            const body: Record<string, unknown> = {
                name: eName,
                email: eEmail,
                role: eRole,
                is_active: eActive,
                can_create_circulars: eCanCreateCirculars,
                can_manage_circular_groups: eCanManageCircularGroups,
            };
            if (ePass.trim() !== '') {
                body.password = ePass;
            }
            if (eRole === 'superadministrator') {
                body.is_protected = eProt;
            }
            const { user: updated } = await api<{ user: BiAdminUser }>(`/admin/users/${editId}`, token, { method: 'PATCH', body: JSON.stringify(body) });
            if (updated.id === currentUser.id) {
                setUser((prev) => (prev
                    ? {
                        ...prev,
                        name: updated.name,
                        email: updated.email ?? '',
                        role: updated.role,
                        is_active: updated.is_active,
                        is_protected: updated.is_protected,
                    }
                    : prev));
            }
            cancelEdit();
            await loadUsers();
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function submitAssignments(event: FormEvent) {
        event.preventDefault();
        if (!selectedAssignmentUser) {
            return;
        }
        setAssignmentSaving(true);
        setAssignmentError('');
        setAssignmentSuccess('');
        try {
            await api<{ ok: boolean }>(`/admin/users/${selectedAssignmentUser.id}/assignments`, token, {
                method: 'PATCH',
                body: JSON.stringify({
                    role: aRole,
                    company_ids: aCompanyIds,
                    sector_ids: aSectorIds,
                    department_ids: aDepartmentIds,
                    primary_department_id: aPrimaryDepartmentId,
                    enabled_modules: narrowUserModules ? aModuleKeys : [],
                    // Eksplicitna dodjela skladišta samo ako je override uključen; inače [] = vrati na auto-default.
                    warehouse_codes: aWarehouseOverride ? aWarehouseCodes : [],
                    // Zasebna dodjela pristupa skladištu KOD DOBAVLJAČA (m.70/KD1).
                    storage_supplier_access: aStorageSupplierAccess,
                }),
            });
            setAssignmentUsers((current) => current.map((user) => (user.id === selectedAssignmentUser.id
                ? {
                    ...user,
                    role: aRole,
                    company_ids: aCompanyIds,
                    sector_ids: aSectorIds,
                    department_ids: aDepartmentIds,
                    enabled_modules: narrowUserModules ? aModuleKeys : null,
                }
                : user)));
            setAssignmentSuccess('Dodjele su uspješno spremljene.');
            await loadAssignments();
        } catch (err) {
            setAssignmentError(err instanceof Error ? err.message : 'Greška pri spremanju dodjela.');
        } finally {
            setAssignmentSaving(false);
        }
    }

    async function submitSector(event: FormEvent) {
        event.preventDefault();
        if (!sectorCompanyId || !sectorCode.trim() || !sectorNameHr.trim()) {
            return;
        }
        setStructureError('');
        setStructureSuccess('');
        try {
            await api('/admin/sectors', token, {
                method: 'POST',
                body: JSON.stringify({
                    company_id: Number(sectorCompanyId),
                    code: sectorCode.trim(),
                    name_hr: sectorNameHr.trim(),
                    name_en: sectorNameEn.trim() || null,
                }),
            });
            setSectorCode('');
            setSectorNameHr('');
            setSectorNameEn('');
            setStructureSuccess('Sektor je kreiran.');
            await loadStructure();
        } catch (err) {
            setStructureError(err instanceof Error ? err.message : 'Greška pri kreiranju sektora.');
        }
    }

    function openCreateDepartmentDrawer() {
        if (!structure) {
            return;
        }
        const defaultCompanyId = structure.companies[0]?.id;
        setDepartmentDrawerMode('create');
        setDepartmentDrawerId(null);
        setDepartmentDrawerCompanyId(defaultCompanyId != null ? String(defaultCompanyId) : '');
        setDepartmentDrawerSectorId('');
        setDepartmentDrawerCode('');
        setDepartmentDrawerName('');
        setDepartmentDrawerRegion('');
        setDepartmentDrawerCity('');
    }

    function openEditDepartmentDrawer(id: number) {
        if (!structure) {
            return;
        }
        const row = structure.departments.find((department) => department.id === id);
        if (!row) {
            return;
        }
        setDepartmentDrawerMode('edit');
        setDepartmentDrawerId(id);
        setDepartmentDrawerCompanyId(String(row.company_id));
        setDepartmentDrawerSectorId(row.sector_id ? String(row.sector_id) : '');
        setDepartmentDrawerCode(row.code);
        setDepartmentDrawerName(row.name);
        setDepartmentDrawerRegion(row.region ?? '');
        setDepartmentDrawerCity(row.city ?? '');
    }

    function closeDepartmentDrawer() {
        setDepartmentDrawerMode(null);
        setDepartmentDrawerId(null);
        setDepartmentDrawerBusy(false);
    }

    async function saveDepartmentDrawer(event: FormEvent) {
        event.preventDefault();
        if (!departmentDrawerCompanyId || !departmentDrawerCode.trim() || !departmentDrawerName.trim()) {
            return;
        }
        setDepartmentDrawerBusy(true);
        setStructureError('');
        setStructureSuccess('');
        const payload = {
            company_id: Number(departmentDrawerCompanyId),
            sector_id: departmentDrawerSectorId ? Number(departmentDrawerSectorId) : null,
            code: departmentDrawerCode.trim(),
            name: departmentDrawerName.trim(),
            region: departmentDrawerRegion.trim() || null,
            city: departmentDrawerCity.trim() || null,
        };
        try {
            if (departmentDrawerMode === 'create') {
                await api('/admin/departments', token, { method: 'POST', body: JSON.stringify(payload) });
                setStructureSuccess(locale === 'en' ? 'Department created.' : 'Odjel je kreiran.');
            } else if (departmentDrawerMode === 'edit' && departmentDrawerId) {
                await api(`/admin/departments/${departmentDrawerId}`, token, {
                    method: 'PATCH',
                    body: JSON.stringify({
                        code: payload.code,
                        name: payload.name,
                        sector_id: payload.sector_id,
                        region: payload.region,
                        city: payload.city,
                    }),
                });
                setStructureSuccess(locale === 'en' ? 'Department updated.' : 'Odjel je ažuriran.');
            }
            closeDepartmentDrawer();
            await loadStructure();
        } catch (err) {
            setStructureError(err instanceof Error ? err.message : (locale === 'en' ? 'Department save failed.' : 'Greška pri spremanju odjela.'));
        } finally {
            setDepartmentDrawerBusy(false);
        }
    }

    async function deleteDepartment(id: number) {
        const row = structure?.departments.find((d) => d.id === id);
        const label = row ? `${row.code} — ${row.name}` : `#${id}`;
        if (!confirm(locale === 'en' ? `Delete department ${label}?` : `Obrisati odjel ${label}?`)) {
            return;
        }
        setDepartmentDrawerBusy(true);
        setStructureError('');
        setStructureSuccess('');
        try {
            await api(`/admin/departments/${id}/archive`, token, { method: 'POST', body: JSON.stringify({}) });
            setStructureSuccess(locale === 'en' ? 'Department deleted.' : 'Odjel je obrisan.');
            closeDepartmentDrawer();
            await loadStructure();
        } catch (err) {
            setStructureError(err instanceof Error ? err.message : (locale === 'en' ? 'Department delete failed.' : 'Greška pri brisanju odjela.'));
        } finally {
            setDepartmentDrawerBusy(false);
        }
    }

    async function submitProduct(event: FormEvent) {
        event.preventDefault();
        if (!productNameHr.trim()) {
            return;
        }
        setStructureError('');
        setStructureSuccess('');
        try {
            const created = await api<{ ok: boolean; id?: number }>('/admin/products', token, {
                method: 'POST',
                body: JSON.stringify({
                    name_hr: productNameHr.trim(),
                    // slug se generira automatski iz naziva (backend); cijene/premiumi se rješavaju u m.40.1
                    sku: productSku.trim() || null,
                    fine_weight_g: productFineWeightG.trim() ? Number(productFineWeightG) : null,
                    length_mm: productLengthMm.trim() ? Number(productLengthMm) : null,
                    width_mm: productWidthMm.trim() ? Number(productWidthMm) : null,
                    thickness_mm: productThicknessMm.trim() ? Number(productThicknessMm) : null,
                    diameter_mm: productDiameterMm.trim() ? Number(productDiameterMm) : null,
                    cgm_category: productCategory.trim() || null,
                    cgm_supplier: productSupplier.trim() || null,
                    company_ids: productCompanyIds,
                    sector_ids: productSectorIds,
                }),
            });
            // Foto (ako je odabrana) → upload na novo-kreirani proizvod.
            if (productImageFile && created?.id) {
                const ifd = new FormData();
                ifd.append('image', productImageFile);
                try { await uploadFile(`/admin/products/${created.id}/image`, token, ifd); } catch { /* slika nije kritična za kreiranje */ }
            }
            setProductNameHr('');
            setProductSlug('');
            setProductSku('');
            setProductFineWeightG('');
            setProductLengthMm('');
            setProductWidthMm('');
            setProductThicknessMm('');
            setProductDiameterMm('');
            setProductImageFile(null);
            setProductSalePrice('');
            setProductBuyPrice('');
            setProductPremiumPoslovnica('');
            setProductPremiumSlovenija('');
            setProductPremiumBugarska('');
            setProductCategory('');
            setProductSupplier('');
            setProductCompanyIds([]);
            setProductSectorIds([]);
            setStructureSuccess('Proizvod je kreiran.');
            await loadStructure();
        } catch (err) {
            setStructureError(err instanceof Error ? err.message : 'Greška pri kreiranju proizvoda.');
        }
    }

    function startEditSector(id: number) {
        if (!structure) {
            return;
        }
        const row = structure.sectors.find((sector) => sector.id === id);
        if (!row) {
            return;
        }
        setEditingSectorId(id);
        setEditSectorCode(row.code);
        setEditSectorNameHr(row.name_hr);
        setEditSectorNameEn(row.name_en ?? '');
    }

    async function saveEditSector(event: FormEvent) {
        event.preventDefault();
        if (!editingSectorId) {
            return;
        }
        setStructureError('');
        setStructureSuccess('');
        try {
            await api(`/admin/sectors/${editingSectorId}`, token, {
                method: 'PATCH',
                body: JSON.stringify({
                    code: editSectorCode,
                    name_hr: editSectorNameHr,
                    name_en: editSectorNameEn || null,
                }),
            });
            setEditingSectorId(null);
            setStructureSuccess(locale === 'en' ? 'Sector updated.' : 'Sektor je ažuriran.');
            await loadStructure();
        } catch (err) {
            setStructureError(err instanceof Error ? err.message : (locale === 'en' ? 'Sector update failed.' : 'Greška pri uređivanju sektora.'));
        }
    }

    function startEditProduct(id: number) {
        if (!structure) {
            return;
        }
        const row = structure.products.find((product) => product.id === id);
        if (!row) {
            return;
        }
        setEditingProductId(id);
        setEditProductNameHr(row.name_hr);
        setEditProductSlug(row.slug);
        setEditProductSku(row.sku ?? '');
        setEditProductFineWeightG((row as any).fine_weight_g != null ? String((row as any).fine_weight_g) : '');
        setEditProductPurity((row as any).purity != null ? String((row as any).purity) : '');
        setEditProductLengthMm((row as any).length_mm != null ? String((row as any).length_mm) : '');
        setEditProductWidthMm((row as any).width_mm != null ? String((row as any).width_mm) : '');
        setEditProductThicknessMm((row as any).thickness_mm != null ? String((row as any).thickness_mm) : '');
        setEditProductDiameterMm((row as any).diameter_mm != null ? String((row as any).diameter_mm) : '');
        setEditProductImageFile(null);
        setEditProductSalePrice((row as any).current_sale_price_eur != null ? String((row as any).current_sale_price_eur) : '');
        setEditProductBuyPrice((row as any).current_buy_price_eur != null ? String((row as any).current_buy_price_eur) : '');
        setEditProductPremiumPoslovnica((row as any).premium_poslovnica_eur != null ? String((row as any).premium_poslovnica_eur) : '');
        setEditProductPremiumSlovenija((row as any).premium_slovenija_eur != null ? String((row as any).premium_slovenija_eur) : '');
        setEditProductPremiumBugarska((row as any).premium_bugarska_eur != null ? String((row as any).premium_bugarska_eur) : '');
        // is_active može doći iz DB-a kao int(1/0), string("1"/"0") ili bool — normaliziraj
        {
            const v = (row as any).is_active;
            setEditProductIsActive(!(v === 0 || v === '0' || v === false));
        }
        setEditProductSupplier((row as any).cgm_supplier ?? '');
        setEditProductCategoryId((row as any).category_id ?? '');
        setEditProductMintPartnerId((row as any).mint_partner_id ?? '');
        setEditProductDeliveryTypeId((row as any).delivery_type_id ?? '');
        setEditProductTypeId((row as any).product_type_id ?? '');
        setEditProductKpdSifra((row as any).kpd_sifra ?? '');
        setEditProductKpdNaziv((row as any).kpd_naziv ?? '');
        setEditProductStopaPdv((row as any).stopa_pdv != null ? String((row as any).stopa_pdv) : '');
        setEditProductCompanyIds(structure.product_companies.filter((link) => link.product_id === id).map((link) => link.company_id));
        setEditProductSectorIds(structure.product_sectors.filter((link) => link.product_id === id).map((link) => link.sector_id));
    }

    async function saveEditProduct(event: FormEvent) {
        event.preventDefault();
        if (!editingProductId) {
            return;
        }
        setStructureError('');
        setStructureSuccess('');
        try {
            await api(`/admin/products/${editingProductId}`, token, {
                method: 'PATCH',
                body: JSON.stringify({
                    name_hr: editProductNameHr.trim(),
                    sku: editProductSku.trim() || null,
                    fine_weight_g: editProductFineWeightG.trim() ? Number(editProductFineWeightG) : null,
                    purity: editProductPurity.trim() ? Number(editProductPurity) : null,
                    length_mm: editProductLengthMm.trim() ? Number(editProductLengthMm) : null,
                    width_mm: editProductWidthMm.trim() ? Number(editProductWidthMm) : null,
                    thickness_mm: editProductThicknessMm.trim() ? Number(editProductThicknessMm) : null,
                    diameter_mm: editProductDiameterMm.trim() ? Number(editProductDiameterMm) : null,
                    is_active: editProductIsActive ? 1 : 0,
                    cgm_supplier: editProductSupplier.trim() || null,
                    kpd_sifra: editProductKpdSifra.trim() || null,
                    kpd_naziv: editProductKpdNaziv.trim() || null,
                    stopa_pdv: editProductStopaPdv.trim() ? Number(editProductStopaPdv) : null,
                    category_id: editProductCategoryId !== '' ? Number(editProductCategoryId) : null,
                    mint_partner_id: editProductMintPartnerId !== '' ? Number(editProductMintPartnerId) : null,
                    delivery_type_id: editProductDeliveryTypeId !== '' ? Number(editProductDeliveryTypeId) : null,
                    product_type_id: editProductTypeId !== '' ? Number(editProductTypeId) : null,
                    company_ids: editProductCompanyIds,
                    sector_ids: editProductSectorIds,
                }),
            });
            // Foto (ako je odabrana nova) → upload na postojeći proizvod.
            if (editProductImageFile) {
                const ifd = new FormData();
                ifd.append('image', editProductImageFile);
                try { await uploadFile(`/admin/products/${editingProductId}/image`, token, ifd); } catch { /* slika nije kritична */ }
            }
            setEditingProductId(null);
            setEditProductImageFile(null);
            setStructureSuccess(locale === 'en' ? 'Product updated.' : 'Proizvod je ažuriran.');
            await loadStructure();
        } catch (err) {
            setStructureError(err instanceof Error ? err.message : (locale === 'en' ? 'Product update failed.' : 'Greška pri uređivanju proizvoda.'));
        }
    }

    function startEditCorrection(id: number) {
        const row = correctionsRows.find((correction) => correction.id === id);
        if (!row) {
            return;
        }
        setEditingCorrectionId(id);
        setEditCorrectionCompanyId(String(row.company_id));
        setEditCorrectionCounterpartyId(row.counterparty_id ? String(row.counterparty_id) : '');
        setEditCorrectionDate(row.effective_date);
        setEditCorrectionAmount(String(row.amount));
        setEditCorrectionNote(row.note ?? '');
    }

    async function submitNewCorrection(event: FormEvent) {
        event.preventDefault();
        if (!newCorrectionCompanyId || !newCorrectionDate || !newCorrectionAmount) {
            return;
        }
        setCorrectionsError('');
        try {
            await api('/admin/loan-corrections', token, {
                method: 'POST',
                body: JSON.stringify({
                    company_id: Number(newCorrectionCompanyId),
                    counterparty_id: newCorrectionCounterpartyId ? Number(newCorrectionCounterpartyId) : null,
                    effective_date: newCorrectionDate,
                    amount: Number(newCorrectionAmount),
                    note: newCorrectionNote.trim() || null,
                }),
            });
            setNewCorrectionCounterpartyId('');
            setNewCorrectionDate('');
            setNewCorrectionAmount('');
            setNewCorrectionNote('');
            await loadCorrections();
        } catch (err) {
            setCorrectionsError(err instanceof Error ? err.message : (locale === 'en' ? 'Correction create failed.' : 'Greška pri kreiranju korekcije.'));
        }
    }

    async function saveEditCorrection(event: FormEvent) {
        event.preventDefault();
        if (!editingCorrectionId) {
            return;
        }
        setCorrectionsError('');
        try {
            await api(`/admin/loan-corrections/${editingCorrectionId}`, token, {
                method: 'PATCH',
                body: JSON.stringify({
                    company_id: Number(editCorrectionCompanyId),
                    counterparty_id: editCorrectionCounterpartyId ? Number(editCorrectionCounterpartyId) : null,
                    effective_date: editCorrectionDate,
                    amount: Number(editCorrectionAmount),
                    note: editCorrectionNote.trim() || null,
                    status: 'active',
                }),
            });
            setEditingCorrectionId(null);
            await loadCorrections();
        } catch (err) {
            setCorrectionsError(err instanceof Error ? err.message : (locale === 'en' ? 'Correction update failed.' : 'Greška pri uređivanju korekcije.'));
        }
    }

    async function archiveCorrection(id: number) {
        setCorrectionsError('');
        try {
            await api(`/admin/loan-corrections/${id}/archive`, token, { method: 'PATCH' });
            if (editingCorrectionId === id) {
                setEditingCorrectionId(null);
            }
            await loadCorrections();
        } catch (err) {
            setCorrectionsError(err instanceof Error ? err.message : (locale === 'en' ? 'Archive failed.' : 'Greška pri arhiviranju korekcije.'));
        }
    }

    function toggleFromArray(setter: React.Dispatch<React.SetStateAction<number[]>>, value: number) {
        setter((current) => (current.includes(value) ? current.filter((id) => id !== value) : [...current, value]));
    }

    function toggleUserModuleKey(key: string) {
        // Klik na modul UVIJEK znači osobni filter → auto-uključi ga (inače save pošalje [] i izbor se izgubi).
        setNarrowUserModules(true);
        setAModuleKeys((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]));
    }

    function toggleCompanyModuleKey(companyId: number, key: string) {
        setCompanyModuleDraft((draft) => {
            const row = draft[companyId] ?? [...fullModuleCatalog];
            const next = row.includes(key) ? row.filter((k) => k !== key) : [...row, key];

            return { ...draft, [companyId]: next };
        });
    }

    async function saveCompanyModuleRow(companyId: number) {
        setStructureError('');
        setStructureSuccess('');
        try {
            const keys = companyModuleDraft[companyId] ?? [...fullModuleCatalog];
            const sortedFull = [...fullModuleCatalog].map(String).sort().join('|');
            const sortedSel = [...keys].map(String).sort().join('|');
            let payload: string[] | null;
            if (sortedSel === sortedFull) {
                payload = null;
            } else if (keys.length === 0) {
                payload = [];
            } else {
                payload = keys;
            }
            await api(`/admin/companies/${companyId}/modules`, token, {
                method: 'PATCH',
                body: JSON.stringify({ enabled_modules: payload }),
            });
            setStructureSuccess(locale === 'en' ? 'Company modules updated.' : 'Moduli tvrtke su spremljeni.');
            await loadStructure();
        } catch (err) {
            setStructureError(err instanceof Error ? err.message : (locale === 'en' ? 'Module update failed.' : 'Greška pri spremanju modula tvrtke.'));
        }
    }

    const selectedCompanyCodes = useMemo(
        () => assignmentCompanies.filter((company) => aCompanyIds.includes(company.id)).map((company) => company.code ?? company.name).join(', '),
        [assignmentCompanies, aCompanyIds],
    );

async function impersonateUser(target: BiAdminUser) {
        if (!target.is_active) {
            setLocalError(locale === 'en' ? 'Cannot impersonate inactive user.' : 'Ne možeš impersonirati neaktivnog korisnika.');
            return;
        }
        if (target.id === currentUser.id) {
            return;
        }
        const msg = locale === 'en'
            ? `Open session as ${target.email}? Your current token will be replaced until you stop impersonation.`
            : `Otvoriti sesiju kao ${target.email}? Trenutni token bit će zamijenjen dok ne završiš impersonaciju.`;
        if (!window.confirm(msg)) {
            return;
        }
        setLocalError('');
        try {
            const res = await api<{ token: string; user: User; impersonation: ImpersonationPayload }>(`/admin/impersonate/${target.id}`, token, { method: 'POST' });
            onSessionReplace(res);
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function removeUser(id: number) {
        if (!window.confirm('Obrisati korisnika (soft delete — može se vratiti iz "Arhivirani")?')) {
            return;
        }
        setLocalError('');
        try {
            await api<{ ok: boolean }>(`/admin/users/${id}`, token, { method: 'DELETE' });
            await loadUsers();
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'Greška');
        }
    }

    async function restoreUser(id: number) {
        setLocalError('');
        try {
            await api(`/admin/users/${id}/restore`, token, { method: 'POST' });
            await loadUsers();
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'Greška');
        }
    }

    return (
        <section className="module-stack">
            <section className="card hero-module">
                <div className="module-heading">
                    <div>
                        <p className="eyebrow">Administracija</p>
                        <h2>BI admin panel</h2>
                        {adminSection === 'landing' ? (
                            <p className="muted">
                                {isSuperadmin
                                    ? (locale === 'en'
                                        ? 'Pick a section below or refresh lists.'
                                        : 'Odaberi odjeljak karticom ili osvježi popise.')
                                    : (locale === 'en'
                                        ? 'Pick a structure section below.'
                                        : 'Odaberi odjeljak strukture karticom ispod.')}
                            </p>
                        ) : (() => {
                            const currentDef = adminSectionDefinition(adminSection);
                            const parentDef = currentDef?.parentSectionId ? adminSectionDefinition(currentDef.parentSectionId) : null;
                            return (
                                <nav className="admin-shell-subnav" aria-label={locale === 'en' ? 'Breadcrumb' : 'Putanja'} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: '0.86rem' }}>
                                    <button type="button" onClick={goLanding} style={{
                                        background: 'transparent', border: 0, color: 'var(--ad-ink)', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, fontWeight: 600,
                                    }}>🏛 {locale === 'en' ? 'Administration' : 'Administracija'}</button>
                                    {parentDef ? (
                                        <>
                                            <span style={{ color: 'rgba(0,0,0,0.4)' }}>›</span>
                                            <button type="button" onClick={() => setAdminSection(parentDef.id)} style={{
                                                background: 'transparent', border: 0, color: 'var(--ad-ink)', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, fontWeight: 600,
                                            }}>{parentDef.code} · {locale === 'en' ? parentDef.labelEn : parentDef.labelHr}</button>
                                        </>
                                    ) : null}
                                    <span style={{ color: 'rgba(0,0,0,0.4)' }}>›</span>
                                    <span aria-current="page" style={{ color: 'var(--ad-ink)', padding: '4px 10px', background: 'linear-gradient(135deg, rgba(var(--ad-soft-rgb),0.18), rgba(254,243,199,0.5))', borderRadius: 6, fontWeight: 700 }}>
                                        {adminBreadcrumbTitle}
                                    </span>
                                </nav>
                            );
                        })()}
                    </div>
                    <div className="admin-toolbar">
                        {adminSection === 'landing' ? (
                            <>
                                {isSuperadmin ? (
                                    <button className="secondary compact" type="button" onClick={() => { void loadUsers(); void loadAssignments(); }}>
                                        {locale === 'en' ? 'Refresh users & assignments' : 'Osvježi korisnike i dodjele'}
                                    </button>
                                ) : null}
                                {canManageStructure ? (
                                    <button className="secondary compact" type="button" onClick={() => void loadStructure()}>
                                        {locale === 'en' ? 'Refresh structure' : 'Osvježi strukturu'}
                                    </button>
                                ) : null}
                                {isSuperadmin ? (
                                    <button
                                        className="compact"
                                        type="button"
                                        onClick={() => {
                                            setAdminSection('101');
                                            setShowCreate((v) => !v);
                                            setLocalError('');
                                        }}
                                    >
                                        {showCreate ? (locale === 'en' ? 'Close form' : 'Zatvori obrazac') : (locale === 'en' ? 'New user' : 'Novi korisnik')}
                                    </button>
                                ) : null}
                            </>
                        ) : (
                            <>
                                <button className="secondary compact" type="button" onClick={refreshCurrentSection}>
                                    {locale === 'en' ? 'Refresh this section' : 'Osvježi ovaj odjeljak'}
                                </button>
                                {isSuperadmin && adminSection === '101' ? (
                                    <button className="compact" type="button" onClick={() => { setShowCreate((v) => !v); setLocalError(''); }}>
                                        {showCreate ? (locale === 'en' ? 'Close form' : 'Zatvori obrazac') : (locale === 'en' ? 'New user' : 'Novi korisnik')}
                                    </button>
                                ) : null}
                            </>
                        )}
                    </div>
                </div>
                {localError ? <p className="error">{localError}</p> : null}
            </section>

            {adminSection === 'landing' ? (
                <section className="card nested-card">
                    {activeTheme === null ? (
                        // Razina 1: 4 velike teme.
                        <div className="module-grid admin-landing-grid" role="navigation" aria-label={locale === 'en' ? 'Admin themes' : 'Administracijske teme'}>
                            {visibleThemes.map((th) => {
                                const sectionsCount = themeSectionsForUser(currentUser, th.id).length;
                                return (
                                    <button
                                        key={th.id}
                                        type="button"
                                        className="card module-card admin-landing-card"
                                        onClick={() => setActiveTheme(th.id)}
                                    >
                                        <p className="eyebrow" style={{ fontSize: '1.4rem' }}>{th.icon}</p>
                                        <h2>{locale === 'en' ? th.labelEn : th.labelHr}</h2>
                                        <p className="muted" style={{ fontSize: '0.84rem' }}>
                                            {locale === 'en' ? th.descriptionEn : th.descriptionHr}
                                        </p>
                                        <p className="muted" style={{ fontSize: '0.74rem', marginTop: 6 }}>
                                            {sectionsCount} {locale === 'en'
                                                ? (sectionsCount === 1 ? 'section' : 'sections')
                                                : (sectionsCount === 1 ? 'sekcija' : (sectionsCount < 5 ? 'sekcije' : 'sekcija'))}
                                        </p>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        // Razina 2: sekcije unutar odabrane teme + breadcrumb natrag.
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                                <button
                                    type="button"
                                    onClick={() => setActiveTheme(null)}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid rgba(var(--ad-soft-rgb),0.4)',
                                        padding: '6px 12px',
                                        borderRadius: 8,
                                        cursor: 'pointer',
                                        fontSize: '0.84rem',
                                        color: 'var(--ad-ink)',
                                        fontWeight: 600,
                                    }}
                                >
                                    ← {locale === 'en' ? 'All themes' : 'Sve teme'}
                                </button>
                                {(() => {
                                    const th = visibleThemes.find((x) => x.id === activeTheme);
                                    if (!th) return null;
                                    return (
                                        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>
                                            <span style={{ marginRight: 6 }}>{th.icon}</span>
                                            {locale === 'en' ? th.labelEn : th.labelHr}
                                        </h2>
                                    );
                                })()}
                            </div>
                            <div className="module-grid admin-landing-grid" role="navigation" aria-label={locale === 'en' ? 'Admin sections' : 'Administracijski odjeljci'}>
                                {themeSectionsForUser(currentUser, activeTheme).map((card) => (
                                    <button
                                        key={card.id}
                                        type="button"
                                        className="card module-card admin-landing-card"
                                        onClick={() => setAdminSection(card.id)}
                                        style={{ position: 'relative' }}
                                    >
                                        {card.id === '170' && (notesPersonal > 0 || notesUnread > 0) ? (
                                            <span style={{ position: 'absolute', top: 10, right: 12, background: notesPersonal > 0 ? '#b14040' : '#1f7a36', color: '#fff', borderRadius: 12, padding: '1px 9px', fontSize: '0.74rem', fontWeight: 700 }}>
                                                {notesPersonal > 0 ? `🔔 ${notesPersonal}` : notesUnread}
                                            </span>
                                        ) : null}
                                        <p className="eyebrow">{adminSectionNavLabel(card, locale)}</p>
                                        <h2>{locale === 'en' ? card.labelEn : card.labelHr}</h2>
                                        <p className="muted">{locale === 'en' ? 'Open' : 'Otvori'}</p>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </section>
            ) : null}
            {isSuperadmin && adminSection === '101' && showCreate ? (
                <section className="card nested-card">
                    <h3 style={{ marginTop: 0 }}>Novi korisnik</h3>
                    <form onSubmit={submitCreate}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))', gap: '0.85rem', maxWidth: '60rem' }}>
                            <label className="filter-label" style={{ margin: 0 }}>
                                Ime
                                <input value={cName} onChange={(ev) => setCName(ev.target.value)} required />
                            </label>
                            <label className="filter-label" style={{ margin: 0 }}>
                                Email
                                <input type="email" value={cEmail} onChange={(ev) => setCEmail(ev.target.value)} required />
                            </label>
                            <label className="filter-label" style={{ margin: 0 }}>
                                Lozinka (min. 10 znakova)
                                <input type="password" value={cPass} onChange={(ev) => setCPass(ev.target.value)} required minLength={10} />
                            </label>
                            <label className="filter-label" style={{ margin: 0 }}>
                                Uloga
                                <select value={cRole} onChange={(ev) => setCRole(ev.target.value)}>
                                    {ADMIN_ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                </select>
                            </label>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', marginTop: '0.85rem' }}>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                <input type="checkbox" checked={cActive} onChange={(ev) => setCActive(ev.target.checked)} />
                                <span>Aktivan</span>
                            </label>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: cRole === 'superadministrator' ? 'pointer' : 'not-allowed', opacity: cRole === 'superadministrator' ? 1 : 0.55 }}>
                                <input type="checkbox" checked={cProt} onChange={(ev) => setCProt(ev.target.checked)} disabled={cRole !== 'superadministrator'} />
                                <span>Zaštićen (samo uz superadmin ulogu)</span>
                            </label>
                        </div>
                        <div className="admin-toolbar" style={{ marginTop: '1rem' }}>
                            <button type="submit" className="compact">Spremi</button>
                        </div>
                    </form>
                </section>
            ) : null}

            {isSuperadmin && adminSection === '101' ? <section className="card">
                <div className="bi-module-hero">
                    <div className="bi-module-hero-icon">👤</div>
                    <div className="bi-module-hero-text">
                        <h1>{adminSectionNavLabel(adminSectionDefinition('101')!, locale)}</h1>
                        <p className="muted">{locale === 'en' ? 'Users, registrations and access management.' : 'Korisnici, registracije i upravljanje pristupom.'}</p>
                    </div>
                    <button type="button" onClick={() => copyDeepLink(locale === 'en' ? 'Link copied:' : 'Link kopiran:')} title={locale === 'en' ? 'Copy deep link to current view (module, admin section, selected user)' : 'Kopiraj deep link na trenutni prikaz (modul, sekcija, odabrani korisnik)'} style={{ background: 'transparent', color: 'var(--ad-ink)', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                        🔗 {locale === 'en' ? 'Copy link' : 'Kopiraj link'}
                    </button>
                    <QuickSearchInput value={query} onChange={setQuery} placeholder="Traži ime, email, ulogu, status, matičnu poslovnicu…" />
                </div>
                {pendingUsersCount > 0 || showPendingOnly ? (
                    <button
                        type="button"
                        onClick={() => setShowPendingOnly((v) => !v)}
                        style={{
                            display: 'block', width: '100%', textAlign: 'left',
                            background: showPendingOnly ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'rgba(var(--ad-acc-rgb),0.13)',
                            border: '1px solid rgba(var(--ad-acc-rgb),0.5)',
                            color: showPendingOnly ? '#fffbeb' : 'var(--ad-acc-3)',
                            borderRadius: 10, padding: '10px 14px', marginBottom: '0.8rem', fontWeight: 700, cursor: 'pointer',
                        }}
                    >
                        ⏳ {locale === 'en'
                            ? `${pendingUsersCount} registration(s) waiting for approval`
                            : `${pendingUsersCount} novih registracija čeka odobrenje`}
                        {' — '}
                        {showPendingOnly
                            ? (locale === 'en' ? 'show all users' : 'prikaži sve korisnike')
                            : (locale === 'en' ? 'review now' : 'pregledaj sada')}
                    </button>
                ) : null}
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.8rem', alignItems: 'center' }}>
                    {([
                        { key: 'active', labelHr: 'Aktivni', labelEn: 'Active' },
                        { key: 'archived', labelHr: 'Arhivirani', labelEn: 'Archived' },
                        { key: 'all', labelHr: 'Svi', labelEn: 'All' },
                    ] as const).map((opt) => (
                        <button key={opt.key} type="button" onClick={() => setUserScope(opt.key)} style={{
                            background: userScope === opt.key ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                            color: userScope === opt.key ? '#fffbeb' : 'var(--ad-ink)',
                            border: userScope === opt.key ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                            padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.84rem',
                        }}>{locale === 'en' ? opt.labelEn : opt.labelHr} {userScope === opt.key ? `(${filteredUsers.length})` : ''}</button>
                    ))}
                    <span style={{ width: 1, height: 22, background: 'rgba(var(--ad-soft-rgb),0.4)', margin: '0 6px' }} />
                    <button type="button" onClick={() => setUserViewMode((m) => m === 'flat' ? 'grouped' : 'flat')} style={{
                        background: userViewMode === 'grouped' ? 'linear-gradient(135deg, #1f7a36, #2d8f44)' : 'transparent',
                        color: userViewMode === 'grouped' ? '#fff' : '#1f7a36',
                        border: userViewMode === 'grouped' ? 0 : '1px solid rgba(31,122,54,0.4)',
                        padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.84rem',
                    }} title={locale === 'en' ? 'Toggle group view (Admin / Branches)' : 'Grupiraj po kategoriji (Uprava / Poslovnice)'}>
                        {userViewMode === 'grouped' ? '📊' : '📋'} {locale === 'en' ? 'Group view' : 'Grupirano'}
                    </button>
                </div>
                {loading ? <p className="muted">Učitavam…</p> : null}
                {!loading && selectedUserIds.size > 0 ? (() => {
                    const selectedUsers = filteredUsers.filter((u) => selectedUserIds.has(u.id));
                    const withEmail = selectedUsers.filter((u) => u.email && u.email.trim() !== '');
                    const emails = withEmail.map((u) => u.email!).join(',');
                    const sendBulkMail = () => {
                        if (emails === '') {
                            alert(locale === 'en' ? 'None of the selected users have email.' : 'Nitko od odabranih nema email.');
                            return;
                        }
                        const mailto = `mailto:?bcc=${encodeURIComponent(emails)}`;
                        window.location.href = mailto;
                    };
                    const copyBcc = async () => {
                        try {
                            await navigator.clipboard.writeText(emails);
                            alert(locale === 'en' ? `Copied ${withEmail.length} email(s) to clipboard.` : `Kopirano ${withEmail.length} email-ova u clipboard.`);
                        } catch {
                            // Fallback
                            const ta = document.createElement('textarea');
                            ta.value = emails; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
                            alert(locale === 'en' ? 'Copied (fallback).' : 'Kopirano (fallback).');
                        }
                    };
                    return (
                        <div style={{ marginBottom: '0.8rem', padding: '10px 14px', background: 'linear-gradient(90deg, rgba(31,122,54,0.10), rgba(31,122,54,0.04))', border: '1px solid rgba(31,122,54,0.35)', borderRadius: 8, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: '0.84rem' }}>
                            <strong style={{ color: '#1f7a36' }}>
                                ✅ {selectedUserIds.size} {locale === 'en' ? 'selected' : 'odabrano'}
                                {withEmail.length !== selectedUserIds.size ? <span className="muted" style={{ fontWeight: 400, marginLeft: 6 }}>({withEmail.length} {locale === 'en' ? 'with email' : 'sa mailom'})</span> : null}
                            </strong>
                            <button type="button" onClick={sendBulkMail} disabled={withEmail.length === 0} style={{ background: withEmail.length === 0 ? 'rgba(0,0,0,0.1)' : '#1f7a36', color: '#fff', border: 0, padding: '6px 14px', borderRadius: 6, cursor: withEmail.length === 0 ? 'default' : 'pointer', fontWeight: 600 }}>
                                📧 {locale === 'en' ? `Send group mail (${withEmail.length})` : `Pošalji grupni mail (${withEmail.length})`}
                            </button>
                            <button type="button" onClick={copyBcc} disabled={withEmail.length === 0} style={{ background: 'transparent', color: 'var(--ad-ink)', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                                📋 {locale === 'en' ? 'Copy BCC list' : 'Kopiraj BCC listu'}
                            </button>
                            <button type="button" onClick={clearUserSelection} style={{ background: 'transparent', color: '#b14040', border: '1px solid rgba(177,64,64,0.4)', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                                ✕ {locale === 'en' ? 'Clear' : 'Očisti'}
                            </button>
                            <span className="muted" style={{ marginLeft: 'auto', fontSize: '0.74rem' }} title="Tip: za pravi tracking + slanje s BIS strane koristi m.9 Okružnice.">
                                💡 {locale === 'en' ? 'For tracked sends use m.9 Circulars' : 'Za tracking koristi m.9 Okružnice'}
                            </span>
                        </div>
                    );
                })() : null}

                {!loading && filteredUsers.length > 0 ? (
                    <div style={{ marginBottom: '0.8rem', padding: '8px 12px', background: 'rgba(31,122,54,0.05)', border: '1px solid rgba(31,122,54,0.25)', borderRadius: 6, fontSize: '0.82rem' }}>
                        📒 <strong>{locale === 'en' ? 'Directory (m.170 Imenik)' : 'Imenik (m.170)'}</strong>:
                        {' '}{filteredUsers.filter((u) => u.show_in_directory).length}/{filteredUsers.length} {locale === 'en' ? 'enrolled' : 'uključeno'}
                        <button
                            type="button"
                            onClick={async () => {
                                const ids = filteredUsers.filter((u) => !u.show_in_directory && !((u as any).deleted_at)).map((u) => u.id);
                                if (ids.length === 0) { alert(locale === 'en' ? 'Everyone already enrolled.' : 'Već su svi uključeni.'); return; }
                                if (!confirm(locale === 'en' ? `Enroll ${ids.length} users to Imenik?` : `Uključiti ${ids.length} korisnika u Imenik?`)) return;
                                try {
                                    const r = await api<{ ok: boolean; affected: number }>('/admin/users/bulk-directory', token, { method: 'POST', body: JSON.stringify({ user_ids: ids, show_in_directory: true }) });
                                    await loadUsers();
                                    alert(locale === 'en' ? `Enrolled ${r.affected}.` : `Uključeno ${r.affected}.`);
                                } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
                            }}
                            style={{ marginLeft: 12, background: '#1f7a36', color: '#fff', padding: '4px 12px', borderRadius: 6, border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: '0.78rem' }}
                        >
                            ✓ {locale === 'en' ? 'Enroll all visible' : 'Uključi sve prikazane'}
                        </button>
                        <button
                            type="button"
                            onClick={async () => {
                                const ids = filteredUsers.filter((u) => u.show_in_directory).map((u) => u.id);
                                if (ids.length === 0) { alert(locale === 'en' ? 'Nobody to remove.' : 'Nema koga maknuti.'); return; }
                                if (!confirm(locale === 'en' ? `Remove ${ids.length} users from Imenik?` : `Maknuti ${ids.length} korisnika iz Imenika?`)) return;
                                try {
                                    const r = await api<{ ok: boolean; affected: number }>('/admin/users/bulk-directory', token, { method: 'POST', body: JSON.stringify({ user_ids: ids, show_in_directory: false }) });
                                    await loadUsers();
                                    alert(locale === 'en' ? `Removed ${r.affected}.` : `Maknuto ${r.affected}.`);
                                } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
                            }}
                            style={{ marginLeft: 6, background: 'transparent', color: '#b14040', padding: '4px 12px', borderRadius: 6, border: '1px solid rgba(177,64,64,0.4)', fontWeight: 600, cursor: 'pointer', fontSize: '0.78rem' }}
                        >
                            ✕ {locale === 'en' ? 'Remove all visible' : 'Makni sve prikazane'}
                        </button>
                    </div>
                ) : null}
                {!loading ? (() => {
                    // Helper: jedan red <tr> za usera — reuse za flat i grouped view
                    const renderUserRow = (u: BiAdminUser) => {
                        const isArchived = !!(u as any).deleted_at;
                        const status = u.status ?? 'active';
                        const statusStyle: Record<string, { bg: string; fg: string; label: string }> = {
                            pending: { bg: 'rgba(var(--ad-acc-rgb),0.18)', fg: 'var(--ad-acc-3)', label: locale === 'en' ? 'Pending' : 'Na čekanju' },
                            active: { bg: 'rgba(31,122,54,0.15)', fg: '#1f7a36', label: locale === 'en' ? 'Active' : 'Aktivan' },
                            inactive: { bg: 'rgba(0,0,0,0.08)', fg: '#666', label: locale === 'en' ? 'Inactive' : 'Neaktivan' },
                        };
                        const st = statusStyle[status] ?? statusStyle.active;
                        const isSelected = drawerUserId === u.id;
                        const isChecked = selectedUserIds.has(u.id);
                        return (
                        <tr
                            key={u.id}
                            onClick={() => setDrawerUserId(u.id)}
                            style={{
                                cursor: 'pointer',
                                background: isSelected
                                    ? 'rgba(var(--ad-acc-rgb),0.12)'
                                    : (isChecked ? 'rgba(31,122,54,0.06)' : (isArchived ? 'rgba(0,0,0,0.03)' : (status === 'pending' ? 'rgba(var(--ad-acc-rgb),0.06)' : undefined))),
                                opacity: isArchived ? 0.6 : 1,
                                borderLeft: isSelected ? '3px solid var(--ad-acc)' : (isChecked ? '3px solid #1f7a36' : '3px solid transparent'),
                                transition: 'background 0.1s ease, border-color 0.1s ease',
                            }}
                        >
                            <td style={{ padding: '6px 4px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => toggleUserSelection(u.id)}
                                    style={{ cursor: 'pointer' }}
                                />
                            </td>
                            <td style={{ padding: '6px 4px 6px 10px' }}>
                                <Avatar user={{ name: u.name, profile: { photo_url: u.photo_url ?? null } }} size={36} />
                            </td>
                            <td>
                                <div style={{ fontWeight: 600, color: '#34383d' }}>{u.name}</div>
                                <div className="muted" style={{ fontSize: '0.78rem', marginTop: 2 }}>{u.email ?? '—'}</div>
                                {isArchived ? <span style={{ marginLeft: 0, fontSize: '0.7rem', color: '#b14040', display: 'inline-block', marginTop: 2 }}>🗑 arhiviran</span> : null}
                                {u.is_protected ? <span style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--ad-ink)', fontWeight: 600 }}>🛡</span> : null}
                            </td>
                            <td style={{ fontSize: '0.82rem', color: '#4b5157' }}>{u.company_label ?? <span className="muted">—</span>}</td>
                            <td style={{ fontSize: '0.82rem', color: '#4b5157' }}>
                                {u.primary_department_label ?? <span className="muted">—</span>}
                                {u.home_warehouse_label ? (
                                    <div className="muted" style={{ fontSize: '0.7rem' }} title="Gold skladište (m.40.3)">🏪 {u.home_warehouse_label}</div>
                                ) : null}
                            </td>
                            <td style={{ fontSize: '0.86rem', textAlign: 'center' }}>
                                <span style={{ background: 'rgba(31,122,54,0.12)', color: '#1f7a36', padding: '3px 9px', borderRadius: 8, fontWeight: 700 }}>{u.modules_count ?? 0}</span>
                            </td>
                            <td style={{ fontSize: '0.86rem', color: '#4b5157' }}>{u.role}</td>
                            <td>
                                <span style={{ background: st.bg, color: st.fg, padding: '3px 9px', borderRadius: 7, fontSize: '0.76rem', fontWeight: 700 }}>{st.label}</span>
                            </td>
                            <td style={{ fontSize: '0.82rem', color: '#6b6e74' }}>
                                {u.last_activity_at ? formatDateTime(u.last_activity_at, locale) : <span className="muted">{locale === 'en' ? 'never' : 'nikad'}</span>}
                            </td>
                            <td style={{ textAlign: 'right', paddingRight: 12, color: 'var(--ad-acc)' }}>›</td>
                        </tr>
                        );
                    };

                    const allVisibleSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selectedUserIds.has(u.id));
                    const someVisibleSelected = !allVisibleSelected && filteredUsers.some((u) => selectedUserIds.has(u.id));
                    const toggleAllVisible = () => {
                        if (allVisibleSelected) {
                            clearUserSelection();
                        } else {
                            setSelectedUserIds(new Set(filteredUsers.map((u) => u.id)));
                        }
                    };
                    const tableHeader = (
                        <thead>
                            <tr>
                                <th style={{ width: 30 }} title={locale === 'en' ? 'Select for bulk action' : 'Označi za grupnu akciju'}>
                                    <input
                                        type="checkbox"
                                        checked={allVisibleSelected}
                                        ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                                        onChange={toggleAllVisible}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </th>
                                <th style={{ width: 44 }}></th>
                                {sortTh('name', locale === 'en' ? 'Name' : 'Ime')}
                                {sortTh('company', locale === 'en' ? 'Company' : 'Tvrtka')}
                                {sortTh('home_wh', locale === 'en' ? 'Home branch' : 'Matica poslovnica')}
                                {sortTh('modules', locale === 'en' ? 'Modules' : 'Moduli')}
                                {sortTh('role', locale === 'en' ? 'Role' : 'Uloga')}
                                {sortTh('status', locale === 'en' ? 'Status' : 'Status')}
                                {sortTh('activity', locale === 'en' ? 'Last activity' : 'Zadnja aktivnost')}
                                <th style={{ width: 1 }}></th>
                            </tr>
                        </thead>
                    );

                    if (userViewMode === 'flat') {
                        return (
                            <div className="table-wrap">
                                <table className="admin-user-table" style={{ width: '100%' }}>
                                    {tableHeader}
                                    <tbody>
                                        {sortedUsers.length === 0 ? (
                                            <tr><td colSpan={10} style={{ textAlign: 'center', color: 'rgba(0,0,0,0.5)', padding: '1rem', fontStyle: 'italic' }}>{userScope === 'archived' ? 'Nema arhiviranih korisnika.' : 'Nema rezultata.'}</td></tr>
                                        ) : null}
                                        {sortedUsers.map((u) => renderUserRow(u))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    }

                    // Grouped view
                    return (
                        <div>
                            {userCategoryMeta.map((cat) => {
                                const byCompany = groupedUsers[cat.key];
                                const noRegionLabels = ['Bez regije', 'No region'];
                                const companyKeys = Object.keys(byCompany).sort((a, b) => {
                                    const aNo = noRegionLabels.includes(a);
                                    const bNo = noRegionLabels.includes(b);
                                    if (aNo !== bNo) return aNo ? 1 : -1;
                                    return a.localeCompare(b, locale === 'en' ? 'en' : 'hr', { sensitivity: 'base' });
                                });
                                const total = companyKeys.reduce((s, c) => s + byCompany[c].length, 0);
                                if (total === 0) return null;
                                return (
                                    <section key={cat.key} style={{ marginBottom: 24 }}>
                                        <div style={{
                                            borderBottom: `2px solid ${cat.color}`,
                                            padding: '8px 12px',
                                            marginBottom: 8,
                                            background: `linear-gradient(90deg, ${cat.color}1a, transparent)`,
                                            borderRadius: '6px 6px 0 0',
                                        }}>
                                            <h3 style={{ margin: 0, color: cat.color, fontSize: '1.05rem' }}>
                                                {cat.icon} {locale === 'en' ? cat.labelEn : cat.labelHr}
                                                <span className="muted" style={{ fontSize: '0.78rem', marginLeft: 8, fontWeight: 400 }}>{total}</span>
                                            </h3>
                                        </div>
                                        {companyKeys.map((ckey) => {
                                            const items = byCompany[ckey];
                                            const headerIcon = cat.key === 'poslovnice' ? '🌍' : '🏢';
                                            return (
                                                <div key={ckey} style={{ marginBottom: 12 }}>
                                                    {companyKeys.length > 1 || cat.key === 'poslovnice' ? (
                                                        <div style={{ fontSize: '0.82rem', color: cat.color, fontWeight: 700, margin: '8px 4px 4px' }}>
                                                            {headerIcon} {ckey}
                                                            <span className="muted" style={{ fontSize: '0.72rem', marginLeft: 6, fontWeight: 400 }}>{items.length}</span>
                                                        </div>
                                                    ) : null}
                                                    <div className="table-wrap">
                                                        <table className="admin-user-table" style={{ width: '100%' }}>
                                                            {tableHeader}
                                                            <tbody>{items.map((u) => renderUserRow(u))}</tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </section>
                                );
                            })}
                        </div>
                    );
                })() : null}
                {drawerUserId !== null ? (
                    <UserDetailDrawer
                        user={sortedUsers.find((x) => x.id === drawerUserId) ?? null}
                        currentUser={currentUser}
                        locale={locale}
                        onClose={() => setDrawerUserId(null)}
                        onEdit={(u) => { setDrawerUserId(null); startEdit(u); }}
                        onAssignments={(u) => { setDrawerUserId(null); setAssignmentUserId(u.id); setShowAssignmentsModal(true); }}
                        onPreview={(id) => { setDrawerUserId(null); void openProfilePreview(id); }}
                        onToggleDirectory={(u) => void toggleDirectory(u)}
                        onSetStatus={(id, s) => void setUserStatus(id, s)}
                        onImpersonate={(u) => void impersonateUser(u)}
                        onRestore={(id) => { setDrawerUserId(null); void restoreUser(id); }}
                        onRemove={(id) => { setDrawerUserId(null); void removeUser(id); }}
                    />
                ) : null}
            </section> : null}

            {isSuperadmin && adminSection === '101' && editId != null ? (
                <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={cancelEdit}>
                    <div onClick={(e) => e.stopPropagation()} style={{
                        width: 'min(720px, 96vw)',
                        maxHeight: '94vh',
                        overflowY: 'auto',
                        background: 'linear-gradient(180deg, #fffdf6 0%, var(--ad-bg) 100%)',
                        borderRadius: 16,
                        boxShadow: '0 20px 60px rgba(107,79,28,0.28), 0 0 0 1px rgba(var(--ad-soft-rgb),0.18)',
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, var(--ad-ink) 0%, #8b6b2c 60%, var(--ad-acc) 100%)',
                            padding: '1.1rem 1.5rem',
                            color: '#fffbeb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                            <div>
                                <p style={{ margin: 0, fontSize: '0.72rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,251,235,0.7)' }}>
                                    {locale === 'en' ? 'Edit user' : 'Uredi korisnika'} #{editId}
                                </p>
                                <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.4rem', fontWeight: 700 }}>{eName || '—'}</h2>
                                <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'rgba(255,251,235,0.78)' }}>{eEmail}</p>
                            </div>
                            <button type="button" onClick={cancelEdit} style={{ background: 'transparent', color: '#fffbeb', border: 0, fontSize: '1.7rem', cursor: 'pointer', lineHeight: 1, padding: '4px 8px' }}>×</button>
                        </div>
                        <form onSubmit={submitEdit} style={{ padding: '1.2rem 1.5rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(15rem, 1fr))', gap: '0.85rem' }}>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    Ime
                                    <input value={eName} onChange={(ev) => setEName(ev.target.value)} required />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    Email
                                    <input type="email" value={eEmail} onChange={(ev) => setEEmail(ev.target.value)} required />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    Nova lozinka (prazno = ne mijenjaj)
                                    <input type="password" value={ePass} onChange={(ev) => setEPass(ev.target.value)} autoComplete="new-password" />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    Uloga
                                    <select value={eRole} onChange={(ev) => setERole(ev.target.value)} disabled={isProtectedTarget && currentUser.email !== 'vjakomin@gmail.com'}>
                                        {ADMIN_ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                    </select>
                                </label>
                            </div>
                            <div style={{ display: 'flex', gap: '1.2rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={eActive} onChange={(ev) => setEActive(ev.target.checked)} disabled={editId === currentUser.id} />
                                    <strong style={{ color: eActive ? '#1f7a36' : '#b14040' }}>{eActive ? 'Aktivan' : 'Neaktivan'}</strong>
                                </label>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: eRole === 'superadministrator' ? 'pointer' : 'not-allowed', opacity: eRole === 'superadministrator' ? 1 : 0.55 }}>
                                    <input type="checkbox" checked={eProt} onChange={(ev) => setEProt(ev.target.checked)} disabled={eRole !== 'superadministrator' || (isProtectedTarget && currentUser.email !== 'vjakomin@gmail.com')} />
                                    <span>🛡 Zaštićen</span>
                                </label>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={eCanCreateCirculars} onChange={(ev) => setECanCreateCirculars(ev.target.checked)} />
                                    <span>✏️ {locale === 'en' ? 'May create circulars' : 'Smije kreirati okružnice'}</span>
                                </label>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={eCanManageCircularGroups} onChange={(ev) => setECanManageCircularGroups(ev.target.checked)} />
                                    <span>👥 {locale === 'en' ? 'May manage circular groups' : 'Smije urediti grupe primatelja okružnica'}</span>
                                </label>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, marginTop: '1.4rem', borderTop: '1px solid rgba(var(--ad-soft-rgb),0.2)', paddingTop: '1rem', flexWrap: 'wrap' }}>
                                <button type="button" className="secondary compact" onClick={() => { setAssignmentUserId(editId); cancelEdit(); setShowAssignmentsModal(true); }}>
                                    🔑 {locale === 'en' ? 'Edit assignments' : 'Uredi dodjele'}
                                </button>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <button type="submit" className="compact">💾 Spremi</button>
                                    <button type="button" className="secondary compact" onClick={cancelEdit}>Odustani</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}

            {isSuperadmin && showAssignmentsModal && selectedAssignmentUser ? (
                <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => setShowAssignmentsModal(false)}>
                    <div onClick={(e) => e.stopPropagation()} style={{
                        width: 'min(820px, 96vw)',
                        maxHeight: '94vh',
                        overflowY: 'auto',
                        background: 'linear-gradient(180deg, #fffdf6 0%, var(--ad-bg) 100%)',
                        borderRadius: 16,
                        boxShadow: '0 20px 60px rgba(107,79,28,0.28), 0 0 0 1px rgba(var(--ad-soft-rgb),0.18)',
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, var(--ad-ink) 0%, #8b6b2c 60%, var(--ad-acc) 100%)',
                            padding: '1.1rem 1.5rem',
                            color: '#fffbeb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                            <div>
                                <p style={{ margin: 0, fontSize: '0.72rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,251,235,0.7)' }}>
                                    🔑 {locale === 'en' ? 'Assignments' : 'Dodjele'} · 104
                                </p>
                                <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.4rem', fontWeight: 700 }}>{selectedAssignmentUser.name}</h2>
                                <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'rgba(255,251,235,0.78)' }}>{selectedAssignmentUser.email}</p>
                            </div>
                            <button type="button" onClick={() => setShowAssignmentsModal(false)} style={{ background: 'transparent', color: '#fffbeb', border: 0, fontSize: '1.7rem', cursor: 'pointer', lineHeight: 1, padding: '4px 8px' }}>×</button>
                        </div>
                        <form onSubmit={async (e) => { await submitAssignments(e); setShowAssignmentsModal(false); await loadUsers(); }} style={{ padding: '1.2rem 1.5rem' }}>
                            {assignmentError ? <p className="error">{assignmentError}</p> : null}
                            <CopyAssignmentsFromUser
                                token={token}
                                locale={locale}
                                targetUserId={selectedAssignmentUser?.id ?? null}
                                targetUserName={selectedAssignmentUser?.name ?? ''}
                                candidates={assignmentUsers.filter((u) => u.id !== selectedAssignmentUser?.id)}
                                onApplied={async () => {
                                    setAssignmentSuccess(locale === 'en' ? 'Assignments copied.' : 'Dodjele kopirane.');
                                    await loadAssignments();
                                    await loadUsers();
                                    setShowAssignmentsModal(false);
                                }}
                                onError={(msg) => setAssignmentError(msg)}
                            />
                            <label className="filter-label">
                                Uloga
                                <select value={aRole} onChange={(event) => setARole(event.target.value)}>
                                    {ADMIN_ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                </select>
                            </label>
                            <fieldset className="admin-choice-block">
                                <legend>Tvrtke</legend>
                                <p className="muted" style={{ fontSize: '0.8rem' }}>Odabrano: {selectedCompanyCodes || 'nijedna'}</p>
                                <MultiSelectDropdown
                                    locale={locale}
                                    options={assignmentCompanies.map((c) => ({ value: c.id, label: c.code ? `${c.code} — ${c.name}` : c.name }))}
                                    selected={aCompanyIds}
                                    onChange={(next) => setACompanyIds(next.map((v) => Number(v)))}
                                    emptyLabel={locale === 'en' ? 'Select companies' : 'Odaberi tvrtke…'}
                                />
                            </fieldset>
                            <fieldset className="admin-choice-block">
                                <legend>Sektori</legend>
                                <MultiSelectDropdown
                                    locale={locale}
                                    options={availableSectors.map((s) => ({ value: s.id, label: s.code ? `${s.code} — ${s.name_hr}` : s.name_hr }))}
                                    selected={aSectorIds}
                                    onChange={(next) => setASectorIds(next.map((v) => Number(v)))}
                                    emptyLabel={locale === 'en' ? 'Select sectors' : 'Odaberi sektore…'}
                                />
                            </fieldset>
                            <fieldset className="admin-choice-block">
                                <legend>Odjeli (poslovnice)</legend>
                                <MultiSelectDropdown
                                    locale={locale}
                                    options={availableDepartments.map((d) => ({
                                        value: d.id,
                                        label: d.name,
                                        hint: d.sector_id ? (sectorById.get(d.sector_id)?.code ?? sectorById.get(d.sector_id)?.name_hr ?? 'sektor') : undefined,
                                    }))}
                                    selected={aDepartmentIds}
                                    onChange={(next) => setADepartmentIds(next.map((v) => Number(v)))}
                                    emptyLabel={locale === 'en' ? 'Select departments' : 'Odaberi odjele…'}
                                />
                                <label className="filter-label" style={{ marginTop: 8 }}>
                                    🏪 {locale === 'en' ? 'Primary branch (home)' : 'Matična poslovnica'}
                                    <select value={aPrimaryDepartmentId ?? ''} onChange={(e) => setAPrimaryDepartmentId(e.target.value ? Number(e.target.value) : null)}>
                                        <option value="">{locale === 'en' ? '— Not set —' : '— Nije postavljena —'}</option>
                                        {availableDepartments.filter((d) => aDepartmentIds.includes(d.id)).map((d) => (
                                            <option key={d.id} value={d.id}>{d.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <p className="muted" style={{ fontSize: '0.74rem', marginTop: 4 }}>
                                    {locale === 'en' ? 'One of the assigned branches — used for auto-routing reservations (module 40.3).' : 'Mora biti jedna od dodijeljenih — koristi se za auto-rezervacije u modulu 40.3.'}
                                </p>
                            </fieldset>
                            <WarehouseAssignmentFieldset
                                locale={locale}
                                warehouses={assignmentWarehouses}
                                isAdmin={aWarehouseIsAdmin}
                                override={aWarehouseOverride}
                                codes={aWarehouseCodes}
                                defaultCodes={aDefaultWarehouseCodes}
                                onToggleOverride={(on) => { setAWarehouseOverride(on); if (on && aWarehouseCodes.length === 0) setAWarehouseCodes(aDefaultWarehouseCodes); }}
                                onChangeCodes={(next) => setAWarehouseCodes(next)}
                            />
                            <fieldset className="admin-choice-block">
                                <legend>{locale === 'en' ? 'Storage — SUPPLIER (KD1)' : 'Pohrana — KOD DOBAVLJAČA (KD1)'}</legend>
                                <label className="filter-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <input type="checkbox" checked={aStorageSupplierAccess} onChange={(e) => setAStorageSupplierAccess(e.target.checked)} />
                                    {locale === 'en' ? 'Access to the "KOD DOBAVLJAČA" warehouse (m.70 daily transfer report + reconciliation)' : 'Pristup skladištu „KOD DOBAVLJAČA" (m.70 dnevni izvještaj preskladištenja + rekonsilijacija)'}
                                </label>
                            </fieldset>
                            <fieldset className="admin-choice-block">
                                <legend>{locale === 'en' ? 'Modules (user)' : 'Moduli (korisnik)'}</legend>
                                <p className="muted" style={{ fontSize: '0.8rem' }}>
                                    {locale === 'en' ? 'Leave unchecked to follow company union only.' : 'Bez kvačice = pravila tvrtki samo. S kvačicom = dodatni osobni filter.'}
                                </p>
                                <label className="filter-label">
                                    <input
                                        type="checkbox"
                                        checked={narrowUserModules}
                                        onChange={(event) => {
                                            const next = event.target.checked;
                                            setNarrowUserModules(next);
                                            if (!selectedAssignmentUser) return;
                                            const catalog = selectedAssignmentUser.company_module_catalog.length > 0
                                                ? selectedAssignmentUser.company_module_catalog
                                                : [...fullModuleCatalog];
                                            if (next) {
                                                setAModuleKeys(selectedAssignmentUser.enabled_modules?.length ? (selectedAssignmentUser.enabled_modules ?? []) : catalog);
                                            } else {
                                                setAModuleKeys(catalog);
                                            }
                                        }}
                                    />
                                    {locale === 'en' ? 'Personal module filter (narrow)' : 'Osobni filter modula'}
                                </label>
                                <div style={{ opacity: narrowUserModules ? 1 : 0.5, pointerEvents: 'auto', marginTop: 6 }}>
                                    <MultiSelectDropdown
                                        locale={locale}
                                        options={fullModuleCatalog.map((key) => ({ value: key, label: moduleLabelForAssignmentKey(key), hint: key }))}
                                        selected={aModuleKeys}
                                        onChange={(next) => {
                                            const nextSet = new Set(next.map((v) => String(v)));
                                            const curSet = new Set(aModuleKeys);
                                            for (const k of fullModuleCatalog) {
                                                if (curSet.has(k) !== nextSet.has(k)) toggleUserModuleKey(k);
                                            }
                                        }}
                                        emptyLabel={locale === 'en' ? 'No modules selected' : 'Nijedan modul'}
                                    />
                                </div>
                            </fieldset>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: '1.2rem', borderTop: '1px solid rgba(var(--ad-soft-rgb),0.2)', paddingTop: '1rem' }}>
                                <button type="submit" className="compact" disabled={assignmentSaving}>💾 {locale === 'en' ? 'Save assignments' : 'Spremi dodjele'}</button>
                                <button type="button" className="secondary compact" onClick={() => setShowAssignmentsModal(false)}>{locale === 'en' ? 'Cancel' : 'Odustani'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}

            {isSuperadmin && adminSection === '104' ? (
                <section className="card admin-block">
                    <div className="module-heading">
                        <div>
                            <h3>{adminSectionNavLabel(adminSectionDefinition('104')!, locale)}</h3>
                            <p className="muted">Dodjela role i pristupa po tvrtkama, sektorima i odjelima.</p>
                        </div>
                        <QuickSearchInput value={assignmentQuery} onChange={setAssignmentQuery} placeholder="Traži korisnika za dodjelu…" />
                    </div>
                    {assignmentLoading ? <p className="muted">Učitavam dodjele…</p> : null}
                    {assignmentError ? <p className="error">{assignmentError}</p> : null}
                    {assignmentSuccess ? <p className="muted">{assignmentSuccess}</p> : null}
                    {!assignmentLoading && assignmentUsers.length > 0 ? (
                        <div className="admin-layout-grid">
                            <aside className="admin-selection-list">
                                {filteredAssignmentUsers.map((assignmentUser) => (
                                    <button
                                        key={assignmentUser.id}
                                        type="button"
                                        className={assignmentUser.id === assignmentUserId ? 'secondary compact active' : 'secondary compact'}
                                        onClick={() => setAssignmentUserId(assignmentUser.id)}
                                    >
                                        <strong>{assignmentUser.name}</strong>
                                        <span className="muted">{assignmentUser.email}</span>
                                    </button>
                                ))}
                            </aside>
                            <form className="admin-assignment-form" onSubmit={submitAssignments}>
                                <CopyAssignmentsFromUser
                                    token={token}
                                    locale={locale}
                                    targetUserId={selectedAssignmentUser?.id ?? null}
                                    targetUserName={selectedAssignmentUser?.name ?? ''}
                                    candidates={assignmentUsers.filter((u) => u.id !== selectedAssignmentUser?.id)}
                                    onApplied={async () => {
                                        setAssignmentSuccess('Dodjele uspješno kopirane.');
                                        await loadAssignments();
                                    }}
                                    onError={(msg) => setAssignmentError(msg)}
                                />
                                <label className="filter-label">
                                    Uloga
                                    <select value={aRole} onChange={(event) => setARole(event.target.value)}>
                                        {ADMIN_ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                    </select>
                                </label>
                                <fieldset className="admin-choice-block">
                                    <legend>Tvrtke</legend>
                                    <p className="muted">Odabrano: {selectedCompanyCodes || 'nijedna'}</p>
                                    <MultiSelectDropdown
                                        locale={locale}
                                        options={assignmentCompanies.map((c) => ({ value: c.id, label: c.code ? `${c.code} — ${c.name}` : c.name }))}
                                        selected={aCompanyIds}
                                        onChange={(next) => setACompanyIds(next.map((v) => Number(v)))}
                                        emptyLabel={locale === 'en' ? 'Select companies' : 'Odaberi tvrtke…'}
                                    />
                                </fieldset>
                                <fieldset className="admin-choice-block">
                                    <legend>Sektori</legend>
                                    <MultiSelectDropdown
                                        locale={locale}
                                        options={availableSectors.map((s) => ({ value: s.id, label: s.code ? `${s.code} — ${s.name_hr}` : s.name_hr }))}
                                        selected={aSectorIds}
                                        onChange={(next) => setASectorIds(next.map((v) => Number(v)))}
                                        emptyLabel={locale === 'en' ? 'Select sectors' : 'Odaberi sektore…'}
                                    />
                                </fieldset>
                                <fieldset className="admin-choice-block">
                                    <legend>Odjeli</legend>
                                    <MultiSelectDropdown
                                        locale={locale}
                                        options={availableDepartments.map((d) => ({
                                            value: d.id,
                                            label: d.name,
                                            hint: d.sector_id ? (sectorById.get(d.sector_id)?.code ?? sectorById.get(d.sector_id)?.name_hr ?? 'sektor') : undefined,
                                        }))}
                                        selected={aDepartmentIds}
                                        onChange={(next) => setADepartmentIds(next.map((v) => Number(v)))}
                                        emptyLabel={locale === 'en' ? 'Select departments' : 'Odaberi odjele…'}
                                    />
                                </fieldset>
                                <WarehouseAssignmentFieldset
                                    locale={locale}
                                    warehouses={assignmentWarehouses}
                                    isAdmin={aWarehouseIsAdmin}
                                    override={aWarehouseOverride}
                                    codes={aWarehouseCodes}
                                    defaultCodes={aDefaultWarehouseCodes}
                                    onToggleOverride={(on) => { setAWarehouseOverride(on); if (on && aWarehouseCodes.length === 0) setAWarehouseCodes(aDefaultWarehouseCodes); }}
                                    onChangeCodes={(next) => setAWarehouseCodes(next)}
                                />
                                <fieldset className="admin-choice-block">
                                    <legend>{locale === 'en' ? 'Storage — SUPPLIER (KD1)' : 'Pohrana — KOD DOBAVLJAČA (KD1)'}</legend>
                                    <label className="filter-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <input type="checkbox" checked={aStorageSupplierAccess} onChange={(e) => setAStorageSupplierAccess(e.target.checked)} />
                                        {locale === 'en' ? 'Access to the "KOD DOBAVLJAČA" warehouse (m.70 daily transfer report + reconciliation)' : 'Pristup skladištu „KOD DOBAVLJAČA" (m.70 dnevni izvještaj preskladištenja + rekonsilijacija)'}
                                    </label>
                                </fieldset>
                                <fieldset className="admin-choice-block">
                                    <legend>{locale === 'en' ? 'Modules (user)' : 'Moduli (korisnik)'}</legend>
                                    <p className="muted">
                                        {locale === 'en'
                                            ? 'Leave unchecked to follow company module union only. Check to apply an additional personal filter.'
                                            : 'Bez kvačice = vrijede samo pravila tvrtki. Uključi za dodatni osobni odabir modula.'}
                                    </p>
                                    <label className="filter-label">
                                        <input
                                            type="checkbox"
                                            checked={narrowUserModules}
                                            onChange={(event) => {
                                                const next = event.target.checked;
                                                setNarrowUserModules(next);
                                                if (!selectedAssignmentUser) {
                                                    return;
                                                }
                                                const catalog = selectedAssignmentUser.company_module_catalog.length > 0
                                                    ? selectedAssignmentUser.company_module_catalog
                                                    : [...fullModuleCatalog];
                                                if (next) {
                                                    setAModuleKeys(selectedAssignmentUser.enabled_modules?.length ? (selectedAssignmentUser.enabled_modules ?? []) : catalog);
                                                } else {
                                                    setAModuleKeys(catalog);
                                                }
                                            }}
                                        />
                                        {locale === 'en' ? 'Personal module filter (narrow)' : 'Osobni filter modula (suženje)'}
                                    </label>
                                    <div style={{ opacity: narrowUserModules ? 1 : 0.5, pointerEvents: 'auto' }}>
                                        <MultiSelectDropdown
                                            locale={locale}
                                            options={fullModuleCatalog.map((key) => ({ value: key, label: moduleLabelForAssignmentKey(key), hint: key }))}
                                            selected={aModuleKeys}
                                            onChange={(next) => {
                                                const nextSet = new Set(next.map((v) => String(v)));
                                                const curSet = new Set(aModuleKeys);
                                                for (const k of fullModuleCatalog) {
                                                    if (curSet.has(k) !== nextSet.has(k)) {
                                                        toggleUserModuleKey(k);
                                                    }
                                                }
                                            }}
                                            emptyLabel={locale === 'en' ? 'No modules selected' : 'Nijedan modul'}
                                        />
                                    </div>
                                </fieldset>
                                <button className="compact" type="submit" disabled={assignmentSaving || !selectedAssignmentUser}>Spremi dodjele</button>
                            </form>
                        </div>
                    ) : null}
                </section>
            ) : null}

            {canManageStructure && adminSection === '108' ? (
                <section className="card admin-block">
                    <div className="module-heading">
                        <div>
                            <h3>{adminSectionNavLabel(adminSectionDefinition('108')!, locale)}</h3>
                            <p className="muted">
                                {locale === 'en'
                                    ? 'All modules checked = no restriction (default). Save per company.'
                                    : 'Svi moduli označeni = bez dodatne restrikcije (zadano). Spremanje je po tvrtki.'}
                            </p>
                        </div>
                    </div>
                    {structureLoading ? <p className="muted">{locale === 'en' ? 'Loading structure…' : 'Učitavam strukturu…'}</p> : null}
                    {structureError ? <p className="error">{structureError}</p> : null}
                    {structureSuccess ? <p className="muted">{structureSuccess}</p> : null}
                    {!structureLoading && structure ? (
                        <div className="admin-structure-stack">
                            {structure.companies.length > 0 ? (
                                <section className="admin-section-card">
                                    <p className="muted" style={{ marginTop: 0, fontSize: '0.85rem' }}>
                                        {locale === 'en'
                                            ? 'Each row saves independently — change modules then click that company\'s Save.'
                                            : 'Svaki redak se sprema neovisno — promijeni module i klikni Spremi za tu tvrtku.'}
                                    </p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        {structure.companies.map((company) => {
                                            const draftKeys = companyModuleDraft[company.id] ?? [...fullModuleCatalog];
                                            return (
                                                <div key={company.id} style={{ display: 'grid', gridTemplateColumns: '14rem 1fr auto', gap: '0.75rem', alignItems: 'center', padding: '0.5rem 0.75rem', borderRadius: 10, background: 'rgba(var(--ad-soft-rgb),0.05)' }}>
                                                    <strong style={{ fontSize: '0.92rem' }}>{company.code}<span className="muted" style={{ fontWeight: 400, marginLeft: 6 }}>· {company.name}</span></strong>
                                                    <MultiSelectDropdown
                                                        locale={locale}
                                                        options={fullModuleCatalog.map((key) => ({ value: key, label: moduleLabelForAssignmentKey(key), hint: key }))}
                                                        selected={draftKeys}
                                                        onChange={(next) => {
                                                            const nextKeys = next.map((v) => String(v));
                                                            const cur = new Set(draftKeys);
                                                            const upd = new Set(nextKeys);
                                                            for (const k of fullModuleCatalog) {
                                                                if (cur.has(k) !== upd.has(k)) {
                                                                    toggleCompanyModuleKey(company.id, k);
                                                                }
                                                            }
                                                        }}
                                                    />
                                                    <button type="button" className="secondary compact" onClick={() => void saveCompanyModuleRow(company.id)}>
                                                        {locale === 'en' ? 'Save' : 'Spremi'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            ) : null}
                        </div>
                    ) : null}
                </section>
            ) : null}

            {canManageStructure && adminSection === '102' ? (
                <section className="card admin-block">
                    <div className="module-heading">
                        <div>
                            <h3>{adminSectionNavLabel(adminSectionDefinition('102')!, locale)}</h3>
                            <p className="muted">{locale === 'en' ? 'Create, edit and review sectors.' : 'Kreiranje, uređivanje i pregled sektora.'}</p>
                        </div>
                    </div>
                    {structureLoading ? <p className="muted">{locale === 'en' ? 'Loading structure…' : 'Učitavam strukturu…'}</p> : null}
                    {structureError ? <p className="error">{structureError}</p> : null}
                    {structureSuccess ? <p className="muted">{structureSuccess}</p> : null}
                    {!structureLoading && structure ? (
                        <div className="admin-structure-stack">
                            <form className="admin-section-card" onSubmit={submitSector}>
                                <h4>{locale === 'en' ? 'New sector' : 'Novi sektor'}</h4>
                                <label className="filter-label">{locale === 'en' ? 'Company' : 'Tvrtka'}
                                    <select value={sectorCompanyId} onChange={(event) => setSectorCompanyId(event.target.value)} required>
                                        {structure.companies.map((company) => <option key={company.id} value={company.id}>{company.code} - {company.name}</option>)}
                                    </select>
                                </label>
                                <label className="filter-label">{locale === 'en' ? 'Code' : 'Kod'}<input value={sectorCode} onChange={(event) => setSectorCode(event.target.value)} required /></label>
                                <label className="filter-label">{locale === 'en' ? 'Name (HR)' : 'Naziv (HR)'}<input value={sectorNameHr} onChange={(event) => setSectorNameHr(event.target.value)} required /></label>
                                <label className="filter-label">{locale === 'en' ? 'Name (EN)' : 'Naziv (EN)'}<input value={sectorNameEn} onChange={(event) => setSectorNameEn(event.target.value)} /></label>
                                <button className="compact" type="submit">{locale === 'en' ? 'Create sector' : 'Kreiraj sektor'}</button>
                            </form>
                            <section className="admin-table-section">
                                <div className="module-heading compact-heading" style={{ flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                                    <h4 style={{ margin: 0 }}>{locale === 'en' ? 'Sectors overview and edit' : 'Pregled i uređivanje sektora'}</h4>
                                    <div style={{ minWidth: '18rem' }}>
                                        <MultiSelectDropdown
                                            locale={locale}
                                            options={structure.companies.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
                                            selected={sectorCompanyFilter}
                                            onChange={setSectorCompanyFilter}
                                            emptyLabel={locale === 'en' ? 'All companies' : 'Sve tvrtke'}
                                        />
                                    </div>
                                    <QuickSearchInput
                                        value={sectorStructureQuery}
                                        onChange={setSectorStructureQuery}
                                        placeholder={locale === 'en' ? 'Search sector…' : 'Pretraga sektora…'}
                                    />
                                </div>
                                <div className="table-wrap">
                                    <table className="admin-user-table">
                                        <thead><tr><th>ID</th><th>{locale === 'en' ? 'Company' : 'Tvrtka'}</th><th>{locale === 'en' ? 'Code' : 'Kod'}</th><th>{locale === 'en' ? 'Name HR' : 'Naziv HR'}</th><th>{locale === 'en' ? 'Name EN' : 'Naziv EN'}</th><th /></tr></thead>
                                        <tbody>
                                            {filteredStructureSectors.map((row) => {
                                                const co = structure.companies.find((c) => Number(c.id) === Number(row.company_id));
                                                return (
                                                    <tr key={row.id}>
                                                        <td>{row.id}</td>
                                                        <td>{co ? `${co.code} — ${co.name}` : `#${row.company_id}`}</td>
                                                        <td>{row.code}</td>
                                                        <td>{row.name_hr}</td>
                                                        <td>{row.name_en ?? ''}</td>
                                                        <td><button type="button" className="secondary compact" onClick={() => startEditSector(row.id)}>{locale === 'en' ? 'Edit' : 'Uredi'}</button></td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                            {editingSectorId ? (
                                <form className="admin-section-card" onSubmit={saveEditSector}>
                                    <h4>{locale === 'en' ? `Edit sector #${editingSectorId}` : `Uredi sektor #${editingSectorId}`}</h4>
                                    <label className="filter-label">{locale === 'en' ? 'Code' : 'Kod'}<input value={editSectorCode} onChange={(event) => setEditSectorCode(event.target.value)} required /></label>
                                    <label className="filter-label">{locale === 'en' ? 'Name (HR)' : 'Naziv (HR)'}<input value={editSectorNameHr} onChange={(event) => setEditSectorNameHr(event.target.value)} required /></label>
                                    <label className="filter-label">{locale === 'en' ? 'Name (EN)' : 'Naziv (EN)'}<input value={editSectorNameEn} onChange={(event) => setEditSectorNameEn(event.target.value)} /></label>
                                    <div className="admin-toolbar">
                                        <button className="compact" type="submit">{locale === 'en' ? 'Save' : 'Spremi'}</button>
                                        <button className="secondary compact" type="button" onClick={() => setEditingSectorId(null)}>{locale === 'en' ? 'Cancel' : 'Odustani'}</button>
                                    </div>
                                </form>
                            ) : null}
                        </div>
                    ) : null}
                </section>
            ) : null}

            {canManageStructure && adminSection === '103' ? (
                <section className="card admin-block">
                    <div className="module-heading">
                        <div>
                            <h3>{adminSectionNavLabel(adminSectionDefinition('103')!, locale)}</h3>
                            <p className="muted">{locale === 'en' ? 'Add, edit and delete departments — drawer on row click.' : 'Dodavanje, uređivanje i brisanje odjela — drawer na klik retka.'}</p>
                        </div>
                        <button type="button" className="compact" onClick={openCreateDepartmentDrawer} disabled={!structure}>
                            + {locale === 'en' ? 'New department' : 'Novi odjel'}
                        </button>
                    </div>
                    {structureLoading ? <p className="muted">{locale === 'en' ? 'Loading structure…' : 'Učitavam strukturu…'}</p> : null}
                    {structureError ? <p className="error">{structureError}</p> : null}
                    {structureSuccess ? <p className="muted">{structureSuccess}</p> : null}
                    {!structureLoading && structure ? (
                        <section className="admin-table-section">
                            <div className="module-heading compact-heading" style={{ flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                                <h4 style={{ margin: 0 }}>{locale === 'en' ? 'Departments' : 'Odjeli'}</h4>
                                <div style={{ minWidth: '18rem' }}>
                                    <MultiSelectDropdown
                                        locale={locale}
                                        options={structure.companies.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
                                        selected={departmentCompanyFilter}
                                        onChange={setDepartmentCompanyFilter}
                                        emptyLabel={locale === 'en' ? 'All companies' : 'Sve tvrtke'}
                                    />
                                </div>
                                <QuickSearchInput
                                    value={departmentStructureQuery}
                                    onChange={setDepartmentStructureQuery}
                                    placeholder={locale === 'en' ? 'Search department…' : 'Pretraga odjela…'}
                                />
                            </div>
                            <div className="table-wrap">
                                <table className="admin-user-table">
                                    <thead><tr><th>ID</th><th>{locale === 'en' ? 'Company' : 'Tvrtka'}</th><th>{locale === 'en' ? 'Sector' : 'Sektor'}</th><th>{locale === 'en' ? 'Code' : 'Kod'}</th><th>{locale === 'en' ? 'Name' : 'Naziv'}</th><th>{locale === 'en' ? 'Region' : 'Regija'}</th><th>{locale === 'en' ? 'City' : 'Grad'}</th></tr></thead>
                                    <tbody>
                                        {filteredStructureDepartments.map((row) => {
                                            const co = structure.companies.find((c) => Number(c.id) === Number(row.company_id));
                                            const se = structure.sectors.find((s) => Number(s.id) === Number(row.sector_id));
                                            const isSelected = departmentDrawerMode === 'edit' && departmentDrawerId === row.id;
                                            return (
                                                <tr
                                                    key={row.id}
                                                    onClick={() => openEditDepartmentDrawer(row.id)}
                                                    style={{ cursor: 'pointer', background: isSelected ? 'rgba(var(--ad-acc-rgb),0.12)' : undefined }}
                                                >
                                                    <td>{row.id}</td>
                                                    <td>{co ? `${co.code} — ${co.name}` : `#${row.company_id}`}</td>
                                                    <td>{se ? `${se.code} — ${se.name_hr}` : '—'}</td>
                                                    <td>{row.code}</td>
                                                    <td>{row.name}</td>
                                                    <td>{row.region ?? <span className="muted">—</span>}</td>
                                                    <td>{row.city ?? <span className="muted">—</span>}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    ) : null}
                    {departmentDrawerMode ? (
                        <DepartmentDrawer
                            locale={locale}
                            mode={departmentDrawerMode}
                            departmentId={departmentDrawerId}
                            busy={departmentDrawerBusy}
                            companies={structure?.companies ?? []}
                            sectors={availableDepartmentSectors}
                            regionSuggestions={Array.from(new Set((structure?.departments ?? []).map((d) => d.region).filter(Boolean))) as string[]}
                            citySuggestions={Array.from(new Set((structure?.departments ?? []).map((d) => d.city).filter(Boolean))) as string[]}
                            companyId={departmentDrawerCompanyId}
                            sectorId={departmentDrawerSectorId}
                            code={departmentDrawerCode}
                            name={departmentDrawerName}
                            region={departmentDrawerRegion}
                            city={departmentDrawerCity}
                            onCompanyIdChange={(v) => { setDepartmentDrawerCompanyId(v); setDepartmentDrawerSectorId(''); }}
                            onSectorIdChange={setDepartmentDrawerSectorId}
                            onCodeChange={setDepartmentDrawerCode}
                            onNameChange={setDepartmentDrawerName}
                            onRegionChange={setDepartmentDrawerRegion}
                            onCityChange={setDepartmentDrawerCity}
                            onClose={closeDepartmentDrawer}
                            onSave={saveDepartmentDrawer}
                            onDelete={departmentDrawerId ? () => void deleteDepartment(departmentDrawerId) : undefined}
                        />
                    ) : null}
                </section>
            ) : null}

            {canManageStructure && adminSection === '110' ? (
                <section className="card admin-block">
                    <div className="module-heading">
                        <div>
                            <h3>{adminSectionNavLabel(adminSectionDefinition('110')!, locale)}</h3>
                            <p className="muted">{locale === 'en' ? 'Create, edit and review products.' : 'Kreiranje, uređivanje i pregled proizvoda.'}</p>
                        </div>
                    </div>
                    {(() => {
                        const children = childSectionsForUser(currentUser, '110');
                        if (children.length === 0) return null;
                        return (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                gap: '0.75rem',
                                margin: '0.8rem 0 1.2rem',
                            }}>
                                {children.map((c) => (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => setAdminSection(c.id)}
                                        style={{
                                            textAlign: 'left',
                                            background: 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.55) 100%)',
                                            border: '1.5px solid rgba(var(--ad-soft-rgb),0.32)',
                                            borderRadius: 12,
                                            padding: '0.85rem 1rem',
                                            cursor: 'pointer',
                                            transition: 'all 140ms ease',
                                            boxShadow: '0 1px 3px rgba(107,79,28,0.06)',
                                        }}
                                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 14px rgba(var(--ad-acc-rgb),0.18)'; }}
                                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 3px rgba(107,79,28,0.06)'; }}
                                    >
                                        <p className="eyebrow" style={{ margin: 0, fontSize: '0.74rem', color: 'var(--ad-ink)', letterSpacing: '0.08em' }}>{c.code}</p>
                                        <strong style={{ display: 'block', marginTop: 2, color: 'var(--ad-ink)', fontSize: '0.98rem' }}>{locale === 'en' ? c.labelEn : c.labelHr}</strong>
                                        <span style={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)' }}>{locale === 'en' ? 'Open →' : 'Otvori →'}</span>
                                    </button>
                                ))}
                            </div>
                        );
                    })()}
                    {structureLoading ? <p className="muted">{locale === 'en' ? 'Loading structure…' : 'Učitavam strukturu…'}</p> : null}
                    {structureError ? <p className="error">{structureError}</p> : null}
                    {structureSuccess ? <p className="muted">{structureSuccess}</p> : null}
                    {!structureLoading && structure ? (
                        <div className="admin-structure-stack">
                            <AdminCreateCard
                                title={locale === 'en' ? 'New product' : 'Novi proizvod'}
                                hint={locale === 'en' ? 'Register product, link to companies and sectors.' : 'Unesi proizvod, poveži s tvrtkama i sektorima.'}
                                locale={locale}
                            >
                                <form onSubmit={submitProduct}>
                                    <label className="filter-label">{locale === 'en' ? 'Name (HR)' : 'Naziv (HR)'}<input value={productNameHr} onChange={(event) => setProductNameHr(event.target.value)} required /></label>
                                    <label className="filter-label">SKU<input value={productSku} onChange={(event) => setProductSku(event.target.value)} /></label>
                                    {/* Slug se generira automatski iz naziva (HR). Cijene (web/nabavna) i premiumi (poslovnica/Slovenija/Bugarska) se rješavaju u m.40.1 (Gold CM). */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                                        <label className="filter-label" style={{ margin: 0 }}>{locale === 'en' ? 'Fine weight (g)' : 'Fina težina (g)'}<input value={productFineWeightG} type="number" step="0.001" min="0" onChange={(e) => setProductFineWeightG(e.target.value)} placeholder="31.1" /></label>
                                        <label className="filter-label" style={{ margin: 0 }}>{locale === 'en' ? 'Diameter (mm)' : 'Promjer (mm)'}<input value={productDiameterMm} type="number" step="0.01" min="0" onChange={(e) => setProductDiameterMm(e.target.value)} placeholder="zlatnik" /></label>
                                        <label className="filter-label" style={{ margin: 0 }}>{locale === 'en' ? 'Thickness (mm)' : 'Debljina (mm)'}<input value={productThicknessMm} type="number" step="0.01" min="0" onChange={(e) => setProductThicknessMm(e.target.value)} placeholder="0.00" /></label>
                                        <label className="filter-label" style={{ margin: 0 }}>{locale === 'en' ? 'Length (mm)' : 'Duljina (mm)'}<input value={productLengthMm} type="number" step="0.01" min="0" onChange={(e) => setProductLengthMm(e.target.value)} placeholder="poluga" /></label>
                                        <label className="filter-label" style={{ margin: 0 }}>{locale === 'en' ? 'Width (mm)' : 'Širina (mm)'}<input value={productWidthMm} type="number" step="0.01" min="0" onChange={(e) => setProductWidthMm(e.target.value)} placeholder="poluga" /></label>
                                    </div>
                                    <label className="filter-label">{locale === 'en' ? 'Photo (jpg/png/webp)' : 'Fotografija (jpg/png/webp)'}<input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setProductImageFile(e.target.files?.[0] ?? null)} /></label>
                                    <p className="muted" style={{ fontSize: '0.72rem', margin: '2px 0 0' }}>{locale === 'en' ? 'Dimensions drive the 3D render (bar: L×W×thickness, coin: diameter×thickness).' : 'Dimenzije pokreću 3D prikaz (poluga: duljina×širina×debljina, zlatnik: promjer×debljina).'}</p>
                                    <label className="filter-label">{locale === 'en' ? 'Category' : 'Kategorija'}
                                        <input list="product-cat-list" value={productCategory} onChange={(event) => setProductCategory(event.target.value)} placeholder={locale === 'en' ? 'Pick or type new' : 'Odaberi ili upiši novu'} />
                                        <datalist id="product-cat-list">
                                            {(structure.product_categories ?? []).map((c) => <option key={c} value={c} />)}
                                        </datalist>
                                    </label>
                                    <label className="filter-label">{locale === 'en' ? 'Supplier' : 'Dobavljač'}
                                        <select value={productSupplier} onChange={(event) => setProductSupplier(event.target.value)}>
                                            <option value="">{locale === 'en' ? '— select —' : '— odaberi —'}</option>
                                            {(structure.product_suppliers ?? []).map((s) => <option key={s.id} value={s.supplier_name}>{s.supplier_name}</option>)}
                                        </select>
                                    </label>
                                    <fieldset className="admin-choice-block">
                                        <legend>{locale === 'en' ? 'Companies' : 'Tvrtke za proizvod'}</legend>
                                        <MultiSelectDropdown
                                            locale={locale}
                                            options={structure.companies.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
                                            selected={productCompanyIds}
                                            onChange={(next) => setProductCompanyIds(next.map((v) => Number(v)))}
                                            emptyLabel={locale === 'en' ? 'Select companies' : 'Odaberi tvrtke…'}
                                        />
                                    </fieldset>
                                    <fieldset className="admin-choice-block">
                                        <legend>{locale === 'en' ? 'Sectors' : 'Sektori za proizvod'}</legend>
                                        <MultiSelectDropdown
                                            locale={locale}
                                            options={availableProductSectors.map((s) => ({ value: s.id, label: `${s.code} — ${s.name_hr}` }))}
                                            selected={productSectorIds}
                                            onChange={(next) => setProductSectorIds(next.map((v) => Number(v)))}
                                            emptyLabel={locale === 'en' ? 'Select sectors' : 'Odaberi sektore…'}
                                        />
                                    </fieldset>
                                    <button className="compact" type="submit">{locale === 'en' ? 'Create product' : 'Kreiraj proizvod'}</button>
                                </form>
                            </AdminCreateCard>
                            <section className="admin-table-section">
                                <div className="module-heading compact-heading" style={{ flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                                    <h4 style={{ margin: 0 }}>{locale === 'en' ? 'Products overview and edit' : 'Pregled i uređivanje proizvoda'}</h4>
                                    <QuickSearchInput
                                        value={productStructureQuery}
                                        onChange={setProductStructureQuery}
                                        placeholder={locale === 'en' ? 'Search product…' : 'Pretraga proizvoda…'}
                                    />
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.82rem' }}>
                                        <input type="checkbox" checked={productShowArchived} onChange={(e) => setProductShowArchived(e.target.checked)} />
                                        {locale === 'en' ? 'Show archived' : 'Prikaži arhivirane'}
                                    </label>
                                    <label className="filter-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 12 }}>
                                        <input type="checkbox" checked={productShowInactive} onChange={(e) => setProductShowInactive(e.target.checked)} />
                                        {locale === 'en' ? 'Show inactive too' : 'Pokaži i neaktivne'}
                                    </label>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', margin: '0.4rem 0' }}>
                                    <div style={{ minWidth: 200 }}>
                                        <label style={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)' }}>{locale === 'en' ? 'Category filter' : 'Kategorija'}</label>
                                        <MultiSelectDropdown
                                            locale={locale}
                                            options={(structure.product_categories_lookup ?? []).filter((c) => usedCategoryIds.includes(Number(c.id))).map((c) => ({ value: Number(c.id), label: c.name }))}
                                            selected={productFilterCategoryIds}
                                            onChange={(next) => setProductFilterCategoryIds(next.map((v) => Number(v)))}
                                            emptyLabel={locale === 'en' ? 'All categories' : 'Sve kategorije'}
                                        />
                                    </div>
                                    <div style={{ minWidth: 200 }}>
                                        <label style={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)' }}>{locale === 'en' ? 'Type filter' : 'Tip proizvoda'}</label>
                                        <MultiSelectDropdown
                                            locale={locale}
                                            options={(structure.product_types ?? []).filter((t) => usedTypeIds.includes(Number(t.id))).map((t) => ({ value: Number(t.id), label: t.name }))}
                                            selected={productFilterTypeIds}
                                            onChange={(next) => setProductFilterTypeIds(next.map((v) => Number(v)))}
                                            emptyLabel={locale === 'en' ? 'All types' : 'Svi tipovi'}
                                        />
                                    </div>
                                    <div style={{ minWidth: 200 }}>
                                        <label style={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)' }}>{locale === 'en' ? 'Mint filter' : 'Kovnica'}</label>
                                        <MultiSelectDropdown
                                            locale={locale}
                                            options={(structure.partners ?? []).filter((p) => usedMintIds.includes(Number(p.id))).map((p) => ({ value: Number(p.id), label: p.name }))}
                                            selected={productFilterMintIds}
                                            onChange={(next) => setProductFilterMintIds(next.map((v) => Number(v)))}
                                            emptyLabel={locale === 'en' ? 'All mints' : 'Sve kovnice'}
                                        />
                                    </div>
                                </div>
                                <div className="table-wrap">
                                    <table className="admin-user-table">
                                        <thead><tr>
                                            <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('id')}>ID {productSortKey === 'id' ? (productSortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                            <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('sku')}>{locale === 'en' ? 'SKU' : 'Šifra'} {productSortKey === 'sku' ? (productSortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                            <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('name_hr')}>{locale === 'en' ? 'Name' : 'Naziv'} {productSortKey === 'name_hr' ? (productSortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                            <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('type')}>{locale === 'en' ? 'Type' : 'Tip'} {productSortKey === 'type' ? (productSortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                            <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('category')}>{locale === 'en' ? 'Category' : 'Kategorija'} {productSortKey === 'category' ? (productSortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                            <th>{locale === 'en' ? 'Delivery' : 'Rok isporuke'}</th>
                                            <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('mint')}>{locale === 'en' ? 'Mint' : 'Kovnica'} {productSortKey === 'mint' ? (productSortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                            <th>{locale === 'en' ? 'Supplier' : 'Dobavljač'}</th>
                                            <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('kpd_sifra')}>{locale === 'en' ? 'KPD' : 'KPD šifra'} {productSortKey === 'kpd_sifra' ? (productSortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                            <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('stopa_pdv')}>{locale === 'en' ? 'PDV %' : 'PDV %'} {productSortKey === 'stopa_pdv' ? (productSortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                            <th>{locale === 'en' ? 'Status' : 'Status'}</th>
                                            <th /></tr></thead>
                                        <tbody>
                                            {filteredStructureProducts.map((row) => {
                                                const isArchived = !!(row as any).deleted_at;
                                                // is_active može biti number 0/1, string "0"/"1", false/true, ili null (defaultiramo active)
                                                const rawActive = (row as any).is_active;
                                                const isInactive = !isArchived && (rawActive === 0 || rawActive === '0' || rawActive === false);
                                                return (
                                                <tr key={row.id} onClick={() => startEditProduct(row.id)} style={{ cursor: 'pointer', ...(isArchived ? { opacity: 0.55 } : isInactive ? { opacity: 0.7, background: 'rgba(177,64,64,0.04)' } : {}) }} title={locale === 'en' ? 'Click for details' : 'Klik za detalje'}>
                                                    <td>{row.id}</td>
                                                    <td>{row.sku ?? '—'}</td>
                                                    <td>{row.name_hr}</td>
                                                    <td>{(structure.product_types ?? []).find((t) => Number(t.id) === Number((row as any).product_type_id))?.name ?? '—'}</td>
                                                    <td>{(structure.product_categories_lookup ?? []).find((c) => Number(c.id) === Number((row as any).category_id))?.name ?? (row as any).cgm_category ?? '—'}</td>
                                                    <td>{(structure.product_delivery_types ?? []).find((d: any) => Number(d.id) === Number((row as any).delivery_type_id))?.name ?? (row as any).cgm_delivery_type ?? '—'}</td>
                                                    <td>{(structure.partners ?? []).find((p) => Number(p.id) === Number((row as any).mint_partner_id))?.name ?? (row as any).cgm_mint ?? '—'}</td>
                                                    <td>{(row as any).cgm_supplier ?? '—'}</td>
                                                    <td>{(row as any).kpd_sifra ?? '—'}</td>
                                                    <td>{(row as any).stopa_pdv != null ? `${(row as any).stopa_pdv}%` : '—'}</td>
                                                    <td>{isArchived ? <span style={{ color: '#b14040', fontSize: '0.78rem' }}>{locale === 'en' ? 'Archived' : 'Arhivirano'}</span> : isInactive ? <span style={{ color: '#b14040', fontSize: '0.78rem' }}>{locale === 'en' ? 'Inactive' : 'Neaktivno'}</span> : <span style={{ color: '#1f7a36', fontSize: '0.78rem' }}>{locale === 'en' ? 'Active' : 'Aktivno'}</span>}</td>
                                                    <td style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
                                                        {isArchived ? (
                                                            <button type="button" className="secondary compact" onClick={async () => { try { await api(`/admin/products/${row.id}/restore`, token, { method: 'POST' }); await loadStructure(); } catch (e) { console.error(e); } }}>{locale === 'en' ? 'Restore' : 'Dearhiviraj'}</button>
                                                        ) : (
                                                            <button type="button" className="secondary compact" onClick={async () => { if (! confirm(locale === 'en' ? 'Archive this product?' : 'Arhivirati ovaj proizvod?')) return; try { await api(`/admin/products/${row.id}/archive`, token, { method: 'POST' }); await loadStructure(); } catch (e) { console.error(e); } }}>{locale === 'en' ? 'Archive' : 'Arhiviraj'}</button>
                                                        )}
                                                    </td>
                                                </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                            {editingProductId ? (
                                <div
                                    role="dialog"
                                    aria-modal="true"
                                    onClick={() => setEditingProductId(null)}
                                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}
                                >
                                <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(720px, 95vw)', background: '#fff', height: '100vh', overflowY: 'auto', boxShadow: '-4px 0 16px rgba(0,0,0,0.2)', padding: '18px 22px' }}>
                                <form onSubmit={saveEditProduct}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, position: 'sticky', top: 0, background: '#fff', paddingTop: 4, zIndex: 1 }}>
                                        <h4 style={{ margin: 0, color: 'var(--ad-ink)' }}>{locale === 'en' ? `Edit product #${editingProductId}` : `Uredi proizvod #${editingProductId}`}</h4>
                                        <button type="button" onClick={() => setEditingProductId(null)} className="secondary compact" style={{ fontSize: '0.84rem' }}>✕</button>
                                    </div>
                                    <label className="filter-label">{locale === 'en' ? 'Name (HR)' : 'Naziv (HR)'}<input value={editProductNameHr} onChange={(event) => setEditProductNameHr(event.target.value)} required /></label>
                                    <label className="filter-label">SKU<input value={editProductSku} onChange={(event) => setEditProductSku(event.target.value)} /></label>
                                    {/* Slug auto iz naziva; cijene/premiumi se rješavaju u m.40.1 (Gold CM). */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                        <label className="filter-label" style={{ margin: 0 }}>
                                            {locale === 'en' ? 'Fine weight (g)' : 'Fine težina (g)'}
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <input value={editProductFineWeightG} type="number" step="0.001" onChange={(event) => setEditProductFineWeightG(event.target.value)} placeholder="npr. 31.1" style={{ flex: 1 }} />
                                                <button type="button" className="secondary compact" style={{ fontSize: '0.74rem' }}
                                                    title={locale === 'en' ? 'Try to extract from name (e.g. "31.1g" or "1 unca")' : 'Pokušaj izvući iz imena (npr. "31.1g" ili "1 unca")'}
                                                    onClick={() => {
                                                        const name = editProductNameHr;
                                                        const m1 = name.match(/([\d.,]+)\s*g\b/i);
                                                        if (m1) { setEditProductFineWeightG(m1[1].replace(',', '.')); return; }
                                                        const m2 = name.match(/(\d+(?:[.,]\d+)?)\s*unca/i);
                                                        if (m2) {
                                                            const oz = parseFloat(m2[1].replace(',', '.'));
                                                            setEditProductFineWeightG((oz * 31.1034768).toFixed(3));
                                                            return;
                                                        }
                                                        const m3 = name.match(/(\d+)\s*(?:dukat|guld)/i);
                                                        if (m3) {
                                                            // 1 dukat ≈ 3.49g, ali variraju — info-only
                                                            setEditProductFineWeightG((parseInt(m3[1]) * 3.49).toFixed(3));
                                                            return;
                                                        }
                                                        alert(locale === 'en' ? 'Could not extract weight from name.' : 'Ne mogu izvući težinu iz imena.');
                                                    }}
                                                >🔍 {locale === 'en' ? 'Auto' : 'Auto'}</button>
                                            </div>
                                        </label>
                                        <label className="filter-label" style={{ margin: 0 }}>
                                            {locale === 'en' ? 'Purity (0..1)' : 'Finoća (0..1)'}
                                            <input value={editProductPurity} type="number" step="0.0001" min="0" max="1" onChange={(event) => setEditProductPurity(event.target.value)} placeholder="0.999" />
                                        </label>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                                        <label className="filter-label" style={{ margin: 0 }}>{locale === 'en' ? 'Diameter (mm)' : 'Promjer (mm)'}<input value={editProductDiameterMm} type="number" step="0.01" min="0" onChange={(e) => setEditProductDiameterMm(e.target.value)} placeholder="zlatnik" /></label>
                                        <label className="filter-label" style={{ margin: 0 }}>{locale === 'en' ? 'Thickness (mm)' : 'Debljina (mm)'}<input value={editProductThicknessMm} type="number" step="0.01" min="0" onChange={(e) => setEditProductThicknessMm(e.target.value)} placeholder="0.00" /></label>
                                        <label className="filter-label" style={{ margin: 0 }}>{locale === 'en' ? 'Length (mm)' : 'Duljina (mm)'}<input value={editProductLengthMm} type="number" step="0.01" min="0" onChange={(e) => setEditProductLengthMm(e.target.value)} placeholder="poluga" /></label>
                                        <label className="filter-label" style={{ margin: 0 }}>{locale === 'en' ? 'Width (mm)' : 'Širina (mm)'}<input value={editProductWidthMm} type="number" step="0.01" min="0" onChange={(e) => setEditProductWidthMm(e.target.value)} placeholder="poluga" /></label>
                                    </div>
                                    {(() => { const r = structure?.products.find((p) => p.id === editingProductId) as any; const img = r ? (r.image_path ? `/storage/${r.image_path}` : (r.image_url || null)) : null; return img ? <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '2px 0' }}><img src={img} alt="" style={{ width: 56, height: 56, objectFit: 'contain', background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8 }} /><span className="muted" style={{ fontSize: '0.76rem' }}>{locale === 'en' ? 'Current photo' : 'Trenutna fotografija'}</span></div> : null; })()}
                                    <label className="filter-label">{locale === 'en' ? 'Replace photo (jpg/png/webp)' : 'Zamijeni fotografiju (jpg/png/webp)'}<input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setEditProductImageFile(e.target.files?.[0] ?? null)} /></label>
                                    <label className="filter-label" style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                                        <input type="checkbox" checked={editProductIsActive} onChange={(e) => setEditProductIsActive(e.target.checked)} />
                                        {locale === 'en' ? 'Active (visible in Planner/Price lists)' : 'Aktivno (vidljivo u Planeru / cjenicima)'}
                                    </label>
                                    <label className="filter-label">{locale === 'en' ? 'Supplier' : 'Dobavljač'}
                                        <select value={editProductSupplier} onChange={(event) => setEditProductSupplier(event.target.value)}>
                                            <option value="">{locale === 'en' ? '— select —' : '— odaberi —'}</option>
                                            {/* Legacy vrijednost koja nije u registru — zadrži je da se ne izgubi pri spremanju. */}
                                            {editProductSupplier && !(structure.product_suppliers ?? []).some((s) => s.supplier_name === editProductSupplier) ? (
                                                <option value={editProductSupplier}>{editProductSupplier} {locale === 'en' ? '(not in registry)' : '(nije u registru)'}</option>
                                            ) : null}
                                            {(structure.product_suppliers ?? []).map((s) => <option key={s.id} value={s.supplier_name}>{s.supplier_name}</option>)}
                                        </select>
                                    </label>
                                    <fieldset className="admin-choice-block">
                                        <legend>{locale === 'en' ? 'Companies' : 'Tvrtke'}</legend>
                                        <MultiSelectDropdown
                                            locale={locale}
                                            options={structure.companies.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
                                            selected={editProductCompanyIds}
                                            onChange={(next) => setEditProductCompanyIds(next.map((v) => Number(v)))}
                                            emptyLabel={locale === 'en' ? 'Select companies' : 'Odaberi tvrtke…'}
                                        />
                                    </fieldset>
                                    <fieldset className="admin-choice-block">
                                        <legend>{locale === 'en' ? 'Sectors' : 'Sektori'}</legend>
                                        <MultiSelectDropdown
                                            locale={locale}
                                            options={availableEditProductSectors.map((s) => ({ value: s.id, label: `${s.code} — ${s.name_hr}` }))}
                                            selected={editProductSectorIds}
                                            onChange={(next) => setEditProductSectorIds(next.map((v) => Number(v)))}
                                            emptyLabel={locale === 'en' ? 'Select sectors' : 'Odaberi sektore…'}
                                        />
                                    </fieldset>

                                    <h5 style={{ margin: '0.85rem 0 0.4rem', color: 'var(--ad-ink)' }}>{locale === 'en' ? 'Klasifikacija & atributi' : 'Klasifikacija & atributi'}</h5>
                                    <label className="filter-label">{locale === 'en' ? 'Product type' : 'Tip proizvoda'}
                                        <select value={editProductTypeId} onChange={(e) => setEditProductTypeId(e.target.value === '' ? '' : Number(e.target.value))}>
                                            <option value="">—</option>
                                            {(structure.product_types ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                        </select>
                                    </label>
                                    <label className="filter-label">{locale === 'en' ? 'Category' : 'Kategorija proizvoda'}
                                        <select value={editProductCategoryId} onChange={(e) => setEditProductCategoryId(e.target.value === '' ? '' : Number(e.target.value))}>
                                            <option value="">—</option>
                                            {(structure.product_categories_lookup ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                    </label>
                                    <label className="filter-label">{locale === 'en' ? 'Mint' : 'Kovnica'}
                                        <select value={editProductMintPartnerId} onChange={(e) => setEditProductMintPartnerId(e.target.value === '' ? '' : Number(e.target.value))}>
                                            <option value="">—</option>
                                            {(structure.partners ?? []).filter((p) => p.role === 'mint' || p.role === 'both').map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                        </select>
                                    </label>
                                    <label className="filter-label">{locale === 'en' ? 'Delivery type' : 'Rok isporuke'}
                                        <select value={editProductDeliveryTypeId} onChange={(e) => setEditProductDeliveryTypeId(e.target.value === '' ? '' : Number(e.target.value))}>
                                            <option value="">—</option>
                                            {(structure.product_delivery_types ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                        </select>
                                    </label>
                                    <label className="filter-label">{locale === 'en' ? 'KPD šifra' : 'KPD šifra'}<input value={editProductKpdSifra} onChange={(e) => setEditProductKpdSifra(e.target.value)} /></label>
                                    <label className="filter-label">{locale === 'en' ? 'KPD naziv' : 'KPD naziv'}<input value={editProductKpdNaziv} onChange={(e) => setEditProductKpdNaziv(e.target.value)} /></label>
                                    <label className="filter-label">{locale === 'en' ? 'PDV %' : 'Stopa PDV %'}<input type="number" step="0.01" value={editProductStopaPdv} onChange={(e) => setEditProductStopaPdv(e.target.value)} /></label>

                                    <div className="admin-toolbar">
                                        <button className="compact" type="submit">{locale === 'en' ? 'Save' : 'Spremi'}</button>
                                        <button className="secondary compact" type="button" onClick={() => setEditingProductId(null)}>{locale === 'en' ? 'Cancel' : 'Odustani'}</button>
                                    </div>
                                </form>
                                </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </section>
            ) : null}

            {canManageStructure && adminSection === '111' && structure ? (
                <section className="card admin-block">
                    <div className="module-heading">
                        <h3>{locale === 'en' ? 'Partners (mint / supplier)' : 'Partneri (kovnice / dobavljači)'}</h3>
                        <p className="muted">{locale === 'en' ? 'Used as mint or supplier in product master.' : 'Koriste se kao kovnica ili dobavljač u proizvodima.'}</p>
                    </div>
                    <AdminCreateCard title={locale === 'en' ? 'New partner' : 'Novi partner'} hint="" locale={locale}>
                        <form onSubmit={async (e) => { e.preventDefault(); if (! partnerName.trim()) return; try { await api('/admin/partners', token, { method: 'POST', body: JSON.stringify({ name: partnerName.trim(), role: partnerRole, country: partnerCountry || null, contact_email: partnerEmail || null, contact_phone: partnerPhone || null, notes: partnerNotes || null }) }); setPartnerName(''); setPartnerCountry(''); setPartnerEmail(''); setPartnerPhone(''); setPartnerNotes(''); await loadStructure(); } catch (err) { setStructureError(err instanceof Error ? err.message : ''); } }}>
                            <label className="filter-label">{locale === 'en' ? 'Name' : 'Naziv'}<input value={partnerName} onChange={(e) => setPartnerName(e.target.value)} required /></label>
                            <label className="filter-label">{locale === 'en' ? 'Role' : 'Uloga'}
                                <select value={partnerRole} onChange={(e) => setPartnerRole(e.target.value as any)}>
                                    <option value="mint">{locale === 'en' ? 'Mint' : 'Kovnica'}</option>
                                    <option value="supplier">{locale === 'en' ? 'Supplier' : 'Dobavljač'}</option>
                                    <option value="both">{locale === 'en' ? 'Both' : 'Oboje'}</option>
                                    <option value="other">{locale === 'en' ? 'Other' : 'Ostalo'}</option>
                                </select>
                            </label>
                            <label className="filter-label">{locale === 'en' ? 'Country' : 'Država'}<input value={partnerCountry} onChange={(e) => setPartnerCountry(e.target.value)} /></label>
                            <label className="filter-label">Email<input type="email" value={partnerEmail} onChange={(e) => setPartnerEmail(e.target.value)} /></label>
                            <label className="filter-label">{locale === 'en' ? 'Phone' : 'Telefon'}<input value={partnerPhone} onChange={(e) => setPartnerPhone(e.target.value)} /></label>
                            <label className="filter-label">{locale === 'en' ? 'Notes' : 'Napomena'}<textarea value={partnerNotes} onChange={(e) => setPartnerNotes(e.target.value)} /></label>
                            <button className="compact" type="submit">{locale === 'en' ? 'Create partner' : 'Kreiraj partnera'}</button>
                        </form>
                    </AdminCreateCard>
                    <section className="admin-table-section">
                        {(() => {
                            const allPartners = structure.partners ?? [];
                            const roleLabel = (r: string) => r === 'mint' ? (locale === 'en' ? 'Mint' : 'Kovnica') : r === 'supplier' ? (locale === 'en' ? 'Supplier' : 'Dobavljač') : r === 'both' ? (locale === 'en' ? 'Both' : 'Oboje') : (locale === 'en' ? 'Other' : 'Ostalo');
                            const countries = Array.from(new Set(allPartners.map((p) => (p.country ?? '').trim()).filter(Boolean))).sort();
                            const roleCount = (r: string) => allPartners.filter((p) => p.role === r).length;
                            const visiblePartners = allPartners.filter((p) => {
                                if (partnerRoleFilter !== 'all' && p.role !== partnerRoleFilter) return false;
                                if (partnerCountryFilter !== 'all' && (p.country ?? '').trim() !== partnerCountryFilter) return false;
                                if (partnerQuery.trim() && !matchesQuickSearch([p.name, roleLabel(p.role), p.country, p.contact_email, p.contact_phone, p.notes], partnerQuery)) return false;
                                return true;
                            });
                            return (
                        <>
                        <div className="filter-row" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                            <QuickSearchInput value={partnerQuery} onChange={setPartnerQuery} placeholder={locale === 'en' ? 'Search name, country, email…' : 'Traži naziv, državu, email…'} />
                            <label className="filter-label" style={{ margin: 0 }}>{locale === 'en' ? 'Role' : 'Uloga'}
                                <select value={partnerRoleFilter} onChange={(e) => setPartnerRoleFilter(e.target.value as any)} style={{ marginLeft: 6 }}>
                                    <option value="all">{locale === 'en' ? `All (${allPartners.length})` : `Sve (${allPartners.length})`}</option>
                                    <option value="mint">{locale === 'en' ? 'Mint' : 'Kovnica'} ({roleCount('mint')})</option>
                                    <option value="supplier">{locale === 'en' ? 'Supplier' : 'Dobavljač'} ({roleCount('supplier')})</option>
                                    <option value="both">{locale === 'en' ? 'Both' : 'Oboje'} ({roleCount('both')})</option>
                                    <option value="other">{locale === 'en' ? 'Other' : 'Ostalo'} ({roleCount('other')})</option>
                                </select>
                            </label>
                            <label className="filter-label" style={{ margin: 0 }}>{locale === 'en' ? 'Country' : 'Država'}
                                <select value={partnerCountryFilter} onChange={(e) => setPartnerCountryFilter(e.target.value)} style={{ marginLeft: 6 }}>
                                    <option value="all">{locale === 'en' ? 'All' : 'Sve'}</option>
                                    {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </label>
                            {(partnerQuery || partnerRoleFilter !== 'all' || partnerCountryFilter !== 'all') ? (
                                <button type="button" className="secondary compact" onClick={() => { setPartnerQuery(''); setPartnerRoleFilter('all'); setPartnerCountryFilter('all'); }}>↺ {locale === 'en' ? 'Reset' : 'Reset'}</button>
                            ) : null}
                            <span className="muted" style={{ fontSize: '0.78rem', marginLeft: 'auto' }}>{visiblePartners.length} / {allPartners.length}</span>
                        </div>
                        <div className="table-wrap">
                            <table className="admin-user-table">
                                <thead><tr><th>ID</th><th>{locale === 'en' ? 'Name' : 'Naziv'}</th><th>{locale === 'en' ? 'Role' : 'Uloga'}</th><th>{locale === 'en' ? 'Country' : 'Država'}</th><th>Email</th><th>{locale === 'en' ? 'Phone' : 'Telefon'}</th><th /></tr></thead>
                                <tbody>
                                    {visiblePartners.map((p) => (
                                        <tr key={p.id}>
                                            <td>{p.id}</td>
                                            <td>{editingPartnerId === p.id ? <input value={editPartnerName} onChange={(e) => setEditPartnerName(e.target.value)} /> : p.name}</td>
                                            <td>{editingPartnerId === p.id ? (
                                                <select value={editPartnerRole} onChange={(e) => setEditPartnerRole(e.target.value as any)}>
                                                    <option value="mint">Kovnica</option><option value="supplier">Dobavljač</option><option value="both">Oboje</option><option value="other">Ostalo</option>
                                                </select>
                                            ) : (p.role === 'mint' ? 'Kovnica' : p.role === 'supplier' ? 'Dobavljač' : p.role === 'both' ? 'Oboje' : 'Ostalo')}</td>
                                            <td>{editingPartnerId === p.id ? <input value={editPartnerCountry} onChange={(e) => setEditPartnerCountry(e.target.value)} /> : (p.country ?? '—')}</td>
                                            <td>{editingPartnerId === p.id ? <input value={editPartnerEmail} onChange={(e) => setEditPartnerEmail(e.target.value)} /> : (p.contact_email ?? '—')}</td>
                                            <td>{editingPartnerId === p.id ? <input value={editPartnerPhone} onChange={(e) => setEditPartnerPhone(e.target.value)} /> : (p.contact_phone ?? '—')}</td>
                                            <td style={{ display: 'flex', gap: 4 }}>
                                                {editingPartnerId === p.id ? (
                                                    <>
                                                        <button type="button" className="compact" onClick={async () => { try { await api(`/admin/partners/${p.id}`, token, { method: 'PATCH', body: JSON.stringify({ name: editPartnerName.trim(), role: editPartnerRole, country: editPartnerCountry || null, contact_email: editPartnerEmail || null, contact_phone: editPartnerPhone || null, notes: editPartnerNotes || null }) }); setEditingPartnerId(null); await loadStructure(); } catch (err) { console.error(err); } }}>Spremi</button>
                                                        <button type="button" className="secondary compact" onClick={() => setEditingPartnerId(null)}>Odustani</button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button type="button" className="secondary compact" onClick={() => { setEditingPartnerId(p.id); setEditPartnerName(p.name); setEditPartnerRole(p.role as any); setEditPartnerCountry(p.country ?? ''); setEditPartnerEmail(p.contact_email ?? ''); setEditPartnerPhone(p.contact_phone ?? ''); setEditPartnerNotes(p.notes ?? ''); }}>Uredi</button>
                                                        <button type="button" className="secondary compact" onClick={async () => { if (! confirm('Arhivirati?')) return; try { await api(`/admin/partners/${p.id}/archive`, token, { method: 'POST' }); await loadStructure(); } catch (err) { console.error(err); } }}>Arhiviraj</button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {visiblePartners.length === 0 ? <p className="muted" style={{ padding: 12 }}>{locale === 'en' ? 'No partners match the filters.' : 'Nijedan partner ne odgovara filterima.'}</p> : null}
                        </>
                            );
                        })()}
                    </section>
                </section>
            ) : null}

            {canManageStructure && (adminSection === '110.1' || adminSection === '110.2' || adminSection === '110.3') && structure ? (() => {
                const kind = adminSection === '110.1' ? 'types' : adminSection === '110.2' ? 'delivery_types' : 'categories';
                const rows = adminSection === '110.1' ? (structure.product_types ?? []) : adminSection === '110.2' ? (structure.product_delivery_types ?? []) : (structure.product_categories_lookup ?? []);
                const title = adminSection === '110.1' ? (locale === 'en' ? 'Product types' : 'Tipovi proizvoda') : adminSection === '110.2' ? (locale === 'en' ? 'Delivery types' : 'Rokovi isporuke') : (locale === 'en' ? 'Product categories' : 'Kategorije proizvoda');
                return (
                    <section className="card admin-block">
                        <div className="module-heading"><h3>{title}</h3></div>
                        <AdminCreateCard title={locale === 'en' ? `New ${title}` : `Novi unos: ${title}`} hint="" locale={locale}>
                            <form onSubmit={async (e) => { e.preventDefault(); const name = (lookupName[kind] ?? '').trim(); if (! name) return; const body: any = { name }; if (kind === 'delivery_types') { if (lookupDaysMin) body.days_min = Number(lookupDaysMin); if (lookupDaysMax) body.days_max = Number(lookupDaysMax); } try { await api(`/admin/product-lookups/${kind}`, token, { method: 'POST', body: JSON.stringify(body) }); setLookupName({ ...lookupName, [kind]: '' }); setLookupDaysMin(''); setLookupDaysMax(''); await loadStructure(); } catch (err) { setStructureError(err instanceof Error ? err.message : ''); } }}>
                                <label className="filter-label">{locale === 'en' ? 'Name' : 'Naziv'}<input value={lookupName[kind] ?? ''} onChange={(e) => setLookupName({ ...lookupName, [kind]: e.target.value })} required /></label>
                                {kind === 'delivery_types' ? <>
                                    <label className="filter-label">{locale === 'en' ? 'Days min' : 'Dani min'}<input type="number" min="0" value={lookupDaysMin} onChange={(e) => setLookupDaysMin(e.target.value)} /></label>
                                    <label className="filter-label">{locale === 'en' ? 'Days max' : 'Dani max'}<input type="number" min="0" value={lookupDaysMax} onChange={(e) => setLookupDaysMax(e.target.value)} /></label>
                                </> : null}
                                <button className="compact" type="submit">{locale === 'en' ? 'Add' : 'Dodaj'}</button>
                            </form>
                        </AdminCreateCard>
                        <section className="admin-table-section">
                            <div className="table-wrap">
                                <table className="admin-user-table">
                                    <thead><tr><th>ID</th><th>{locale === 'en' ? 'Name' : 'Naziv'}</th>{kind === 'delivery_types' ? <><th>Min</th><th>Max</th></> : null}<th /></tr></thead>
                                    <tbody>
                                        {rows.map((r: any) => (
                                            <tr key={r.id}>
                                                <td>{r.id}</td>
                                                <td>{editingLookup?.kind === kind && editingLookup?.id === r.id ? <input value={editingLookup.name} onChange={(e) => setEditingLookup({ ...editingLookup, name: e.target.value })} /> : r.name}</td>
                                                {kind === 'delivery_types' ? <>
                                                    <td>{editingLookup?.kind === kind && editingLookup?.id === r.id ? <input type="number" value={editingLookup.days_min ?? ''} onChange={(e) => setEditingLookup({ ...editingLookup, days_min: e.target.value })} style={{ width: 64 }} /> : (r.days_min ?? '—')}</td>
                                                    <td>{editingLookup?.kind === kind && editingLookup?.id === r.id ? <input type="number" value={editingLookup.days_max ?? ''} onChange={(e) => setEditingLookup({ ...editingLookup, days_max: e.target.value })} style={{ width: 64 }} /> : (r.days_max ?? '—')}</td>
                                                </> : null}
                                                <td style={{ display: 'flex', gap: 4 }}>
                                                    {editingLookup?.kind === kind && editingLookup?.id === r.id ? (
                                                        <>
                                                            <button type="button" className="compact" onClick={async () => { const body: any = { name: editingLookup.name.trim() }; if (kind === 'delivery_types') { if (editingLookup.days_min) body.days_min = Number(editingLookup.days_min); if (editingLookup.days_max) body.days_max = Number(editingLookup.days_max); } try { await api(`/admin/product-lookups/${kind}/${r.id}`, token, { method: 'PATCH', body: JSON.stringify(body) }); setEditingLookup(null); await loadStructure(); } catch (err) { console.error(err); } }}>Spremi</button>
                                                            <button type="button" className="secondary compact" onClick={() => setEditingLookup(null)}>Odustani</button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button type="button" className="secondary compact" onClick={() => setEditingLookup({ kind, id: r.id, name: r.name, days_min: r.days_min ?? '', days_max: r.days_max ?? '' })}>Uredi</button>
                                                            <button type="button" className="secondary compact" onClick={async () => { if (! confirm('Arhivirati?')) return; try { await api(`/admin/product-lookups/${kind}/${r.id}/archive`, token, { method: 'POST' }); await loadStructure(); } catch (err) { console.error(err); } }}>Arhiviraj</button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </section>
                );
            })() : null}

            {(adminSection === '1211' || adminSection === '1212' || adminSection === '1213') &&
            userMayAccessAdminSection(currentUser, adminSection) ? (
                <AdminInvoiceObligationsSection
                    token={token}
                    locale={locale}
                    reloadTick={invoiceReloadTick}
                    invoiceAdminSection={adminSection}
                    onNavigateToCashflow={onNavigateToCashflow}
                />
            ) : null}

            {userMayAccessAdminSection(currentUser, '122') && adminSection === '122' ? (
                <AdminFinFacilitiesSection token={token} locale={locale} reloadTick={finReloadTick} adminSection="122" />
            ) : null}

            {userMayAccessAdminSection(currentUser, '123') && adminSection === '123' ? (
                <AdminFinFacilitiesSection token={token} locale={locale} reloadTick={finReloadTick} adminSection="123" />
            ) : null}

            {userMayAccessAdminSection(currentUser, '124') && adminSection === '124' ? (
                <AdminFinFacilitiesSection token={token} locale={locale} reloadTick={finReloadTick} adminSection="124" />
            ) : null}

            {userMayAccessAdminSection(currentUser, '128') && adminSection === '128' ? (
                <AdminGoldGuaranteesSection token={token} locale={locale} />
            ) : null}

            {userMayAccessAdminSection(currentUser, '110.4') && adminSection === '110.4' ? (
                <AdminProductsSection token={token} locale={locale} />
            ) : null}

            {userMayAccessAdminSection(currentUser, '125') && adminSection === '125' ? (
                <AdminPurchaseOrderCodesSection token={token} locale={locale} reloadTick={purchaseOrderReloadTick} />
            ) : null}

            {userMayAccessAdminSection(currentUser, '112') && adminSection === '112' ? (
                <AdminPartnersSection token={token} locale={locale} reloadTick={purchaseOrderReloadTick} />
            ) : null}

            {userMayAccessAdminSection(currentUser, '113') && adminSection === '113' ? (
                <AdminPartnerMappingSection token={token} locale={locale} />
            ) : null}

            {userMayAccessAdminSection(currentUser, '126') && adminSection === '126' ? (
                <AdminE2PersonsSection token={token} locale={locale} reloadTick={e2RegistryReloadTick} />
            ) : null}

            {userMayAccessAdminSection(currentUser, '127') && adminSection === '127' ? (
                <AdminE2ProjectsSection token={token} locale={locale} reloadTick={e2RegistryReloadTick} />
            ) : null}

            {isSuperadmin && adminSection === '140' ? (
                <AdminDocDocSection token={token} locale={locale} />
            ) : null}

            {isSuperadmin && adminSection === '128' ? (
                <AdminGoldWarehousesSection token={token} locale={locale} />
            ) : null}

            {isSuperadmin && adminSection === '129' ? (
                <AdminGoldSuppliersSection token={token} locale={locale} />
            ) : null}

            {isSuperadmin && adminSection === '133' ? (
                <AdminGoldModuleCategoriesSection token={token} locale={locale} />
            ) : null}

            {adminSection === '132' ? (
                <AdminBankAccountsSection token={token} locale={locale} />
            ) : null}

            {isSuperadmin && adminSection === '142' ? (
                <AdminIngestSection token={token} locale={locale} />
            ) : null}

            {isSuperadmin && adminSection === '172' ? (
                <AdminImportAuditSection token={token} locale={locale} />
            ) : null}

            {isSuperadmin && adminSection === '150' ? (
                <AdminPeriodLocksSection token={token} locale={locale} reloadTick={0} />
            ) : null}

            {isSuperadmin && adminSection === '160' ? (
                <AdminAuditLogSection token={token} locale={locale} />
            ) : null}

            {isOwner && adminSection === '180' ? (
                <AdminNotificationRecipientsSection token={token} locale={locale} />
            ) : null}

            {isSuperadmin && adminSection === '190' ? (
                <AdminActivitySection token={token} locale={locale} />
            ) : null}

            {isSuperadmin && adminSection === '170' ? (
                <ModuleNotesLogSection token={token} locale={locale} onSeen={() => setNotesUnread(0)} />
            ) : null}

            {previewLoading || previewUser ? (
                <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => setPreviewUser(null)}>
                    <div onClick={(e) => e.stopPropagation()} style={{
                        width: 'min(720px, 96vw)',
                        maxHeight: '94vh',
                        overflowY: 'auto',
                        background: '#fff',
                        borderRadius: 16,
                        boxShadow: '0 20px 60px rgba(107,79,28,0.28)',
                        padding: '1.5rem 1.8rem',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h3 style={{ margin: 0 }}>👁 Pregled profila</h3>
                            <button type="button" onClick={() => setPreviewUser(null)} className="secondary compact">✕ {locale === 'en' ? 'Close' : 'Zatvori'}</button>
                        </div>
                        {previewLoading ? <p className="muted">{locale === 'en' ? 'Loading…' : 'Učitavam…'}</p> : null}
                        {previewUser ? (() => {
                            const p = previewUser.profile;
                            const photoUrl = p?.photo_url ?? '/img/default-avatar.jpg';
                            return (
                                <div>
                                    <div style={{ display: 'flex', gap: 18, marginBottom: 16, alignItems: 'center' }}>
                                        <img src={photoUrl} alt={previewUser.name} style={{ width: 84, height: 84, borderRadius: '50%', objectFit: 'cover', boxShadow: '0 0 0 2px rgba(var(--ad-soft-rgb),0.4)' }} onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/img/default-avatar.jpg'; }} />
                                        <div>
                                            <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#34383d' }}>{previewUser.name}</div>
                                            <div style={{ fontSize: '0.86rem', color: 'var(--ad-ink)', marginTop: 2 }}>{previewUser.email} · {previewUser.role}</div>
                                            {p && (p as { job_title?: string | null }).job_title ? <div style={{ fontSize: '0.86rem', color: '#34383d', marginTop: 2 }}>💼 {(p as { job_title?: string | null }).job_title}</div> : null}
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px 18px', fontSize: '0.88rem' }}>
                                        <div><span className="muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase' }}>{locale === 'en' ? 'Status' : 'Status'}</span><br /><strong>{previewUser.status ?? 'active'}</strong></div>
                                        <div><span className="muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase' }}>{locale === 'en' ? 'Date of birth' : 'Datum rođenja'}</span><br /><strong>{p?.date_of_birth ? formatDate(p.date_of_birth, locale) : '—'}</strong></div>
                                        <div><span className="muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase' }}>{locale === 'en' ? 'Business phone' : 'Telefon poslovni'}</span><br /><strong>{p?.phone_business ?? '—'}</strong></div>
                                        <div><span className="muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase' }}>{locale === 'en' ? 'Private phone' : 'Telefon privatni'}</span><br /><strong>{p?.phone_private ?? '—'}</strong></div>
                                        <div><span className="muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase' }}>{locale === 'en' ? 'Private email' : 'Privatni email'}</span><br /><strong>{p?.email_private ?? '—'}</strong></div>
                                        <div style={{ gridColumn: '1 / -1' }}><span className="muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase' }}>{locale === 'en' ? 'Address' : 'Adresa'}</span><br /><strong>{p?.address ?? '—'}</strong></div>
                                        <div><span className="muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase' }}>{locale === 'en' ? 'Home address' : 'Kućna adresa'}</span><br /><strong>{p?.address_home ?? '—'}</strong></div>
                                        <div><span className="muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase' }}>{locale === 'en' ? 'Work address' : 'Adresa radnog mjesta'}</span><br /><strong>{p?.address_work ?? '—'}</strong></div>
                                    </div>
                                </div>
                            );
                        })() : null}
                    </div>
                </div>
            ) : null}

        </section>
    );
}


/**
 * M.101 hibrid: side drawer s detaljima i akcijama za odabranog korisnika.
 * Otvara se klikom na red u tablici. Sve akcije idu kroz parent handler-e —
 * Drawer je samo prezentacijski sloj koji čisti tablicu od overflowa atributa.
 */
/**
 * Brzo kopiranje dodjela + ovlasti s drugog korisnika.
 * Klik "📋 Kopiraj postavke od…" → quick search + select → "Primijeni".
 * Postojeće dodjele targeta SE BRIŠU prije kopiranja (s confirm-om).
 */
function CopyAssignmentsFromUser({
    token, locale, targetUserId, targetUserName, candidates, onApplied, onError,
}: {
    token: string;
    locale: 'hr' | 'en';
    targetUserId: number | null;
    targetUserName: string;
    candidates: UserAssignmentRow[];
    onApplied: () => Promise<void>;
    onError: (msg: string) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [sourceId, setSourceId] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return candidates.slice(0, 30);
        return candidates.filter((u) => (u.name + ' ' + u.email + ' ' + u.role).toLowerCase().includes(q)).slice(0, 30);
    }, [candidates, search]);

    const sourceUser = useMemo(() => candidates.find((u) => u.id === sourceId) ?? null, [candidates, sourceId]);

    async function apply() {
        if (!targetUserId || !sourceId) return;
        if (!confirm(t(
            `Kopirati DODJELE i OVLASTI s "${sourceUser?.name}" na "${targetUserName}"?\n\nPostojeće dodjele od ${targetUserName} će se OBRISATI i zamijeniti.`,
            `Copy ASSIGNMENTS and PERMISSIONS from "${sourceUser?.name}" to "${targetUserName}"?\n\nExisting assignments of ${targetUserName} will be DELETED and replaced.`,
        ))) return;
        setBusy(true);
        try {
            await api<{ ok: boolean }>(`/admin/users/${targetUserId}/copy-assignments-from`, token, {
                method: 'POST',
                body: JSON.stringify({ source_user_id: sourceId }),
            });
            setOpen(false);
            setSourceId(null);
            setSearch('');
            await onApplied();
        } catch (e) {
            onError(e instanceof Error ? e.message : t('Greška pri kopiranju.', 'Copy failed.'));
        } finally {
            setBusy(false);
        }
    }

    if (!targetUserId) return null;

    return (
        <div style={{ background: 'rgba(var(--ad-acc-rgb),0.06)', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
            {!open ? (
                <button type="button" className="secondary compact" onClick={() => setOpen(true)} style={{ fontSize: '0.82rem' }}>
                    📋 {t('Kopiraj postavke od drugog korisnika…', 'Copy settings from another user…')}
                </button>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--ad-ink)', fontWeight: 600 }}>
                        📋 {t('Kopiraj postavke od:', 'Copy settings from:')} <span className="muted" style={{ fontWeight: 400 }}>{t('zamijenit će dodjele targeta', 'will replace target assignments')}</span>
                    </div>
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('Traži po imenu / email-u / ulozi…', 'Search by name / email / role…')}
                        style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', fontSize: '0.82rem' }}
                        autoFocus
                    />
                    <select
                        value={sourceId ?? ''}
                        onChange={(e) => setSourceId(e.target.value === '' ? null : Number(e.target.value))}
                        size={Math.min(8, filtered.length + 1)}
                        style={{ padding: '4px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', fontSize: '0.82rem' }}
                    >
                        <option value="">— {t('odaberi izvor', 'select source')} —</option>
                        {filtered.map((u) => (
                            <option key={u.id} value={u.id}>{u.name} · {u.email} · {u.role}</option>
                        ))}
                    </select>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button type="button" className="secondary compact" onClick={() => { setOpen(false); setSourceId(null); setSearch(''); }} style={{ fontSize: '0.78rem' }}>
                            {t('Odustani', 'Cancel')}
                        </button>
                        <button
                            type="button"
                            disabled={busy || !sourceId}
                            onClick={() => void apply()}
                            style={{ background: sourceId ? '#1f7a36' : '#999', color: '#fff', border: 0, padding: '5px 14px', borderRadius: 6, fontSize: '0.82rem', fontWeight: 700, cursor: sourceId ? 'pointer' : 'not-allowed' }}
                        >
                            {busy ? '⏳' : '✓ ' + t('Primijeni', 'Apply')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Per-user dodjela zlatnih skladišta (m.40.3). Default = skladišta poslovnica korisnika
 * (auto-resolved na serveru). Override = ručni odabir. Administrator vidi sva skladišta.
 */
function WarehouseAssignmentFieldset({
    locale, warehouses, isAdmin, override, codes, defaultCodes, onToggleOverride, onChangeCodes,
}: {
    locale: 'hr' | 'en';
    warehouses: AssignmentWarehouse[];
    isAdmin: boolean;
    override: boolean;
    codes: string[];
    defaultCodes: string[];
    onToggleOverride: (on: boolean) => void;
    onChangeCodes: (next: string[]) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const nameOf = (code: string) => warehouses.find((w) => w.code === code)?.name ?? code;
    return (
        <fieldset className="admin-choice-block">
            <legend>🏬 {t('Skladišta (m.40.3 alokacija)', 'Warehouses (m.40.3 allocation)')}</legend>
            {isAdmin ? (
                <p className="muted" style={{ fontSize: '0.78rem' }}>
                    {t('Administrator vidi sva skladišta — dodjela se ne primjenjuje.', 'Administrator sees all warehouses — assignment not applied.')}
                </p>
            ) : (
                <>
                    <label className="filter-label">
                        <input type="checkbox" checked={override} onChange={(e) => onToggleOverride(e.target.checked)} />
                        {' '}{t('Ručno dodijeli skladišta', 'Manually assign warehouses')}
                    </label>
                    {override ? (
                        <MultiSelectDropdown
                            locale={locale}
                            options={warehouses.map((w) => ({ value: w.code, label: `${w.code} · ${w.name}` }))}
                            selected={codes}
                            onChange={(next) => onChangeCodes(next.map(String))}
                            emptyLabel={t('Odaberi skladišta…', 'Select warehouses…')}
                        />
                    ) : (
                        <p className="muted" style={{ fontSize: '0.74rem', marginTop: 4 }}>
                            {t('Zadano (iz poslovnica): ', 'Default (from branches): ')}
                            <strong>
                                {defaultCodes.length > 0
                                    ? defaultCodes.map(nameOf).join(', ')
                                    : t('nijedno — poslovnica nema uparenog skladišta, dodijeli ručno', 'none — branch has no matching warehouse, assign manually')}
                            </strong>
                        </p>
                    )}
                </>
            )}
        </fieldset>
    );
}

/**
 * m.103 — drawer za kreiranje / uređivanje / brisanje odjela.
 */
function DepartmentDrawer({
    locale, mode, departmentId, busy, companies, sectors,
    regionSuggestions, citySuggestions,
    companyId, sectorId, code, name, region, city,
    onCompanyIdChange, onSectorIdChange, onCodeChange, onNameChange, onRegionChange, onCityChange,
    onClose, onSave, onDelete,
}: {
    locale: 'hr' | 'en';
    mode: 'create' | 'edit';
    departmentId: number | null;
    busy: boolean;
    companies: Array<{ id: number; code: string; name: string }>;
    sectors: Array<{ id: number; code: string; name_hr: string }>;
    regionSuggestions: string[];
    citySuggestions: string[];
    companyId: string;
    sectorId: string;
    code: string;
    name: string;
    region: string;
    city: string;
    onCompanyIdChange: (v: string) => void;
    onSectorIdChange: (v: string) => void;
    onCodeChange: (v: string) => void;
    onNameChange: (v: string) => void;
    onRegionChange: (v: string) => void;
    onCityChange: (v: string) => void;
    onClose: () => void;
    onSave: (e: FormEvent) => void;
    onDelete?: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    React.useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') onClose();
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const title = mode === 'create'
        ? t('Novi odjel', 'New department')
        : t(`Odjel #${departmentId ?? ''}`, `Department #${departmentId ?? ''}`);

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(20,16,8,0.32)',
                backdropFilter: 'blur(2px)',
                zIndex: 90,
                display: 'flex',
                justifyContent: 'flex-end',
            }}
        >
            <aside
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: 'min(460px, 96vw)',
                    height: '100%',
                    background: 'linear-gradient(180deg, #fffdf6 0%, var(--ad-bg) 100%)',
                    boxShadow: '-20px 0 60px rgba(107,79,28,0.22), -1px 0 0 rgba(var(--ad-soft-rgb),0.18)',
                    overflowY: 'auto',
                    animation: 'biDrawerSlideIn 0.18s ease-out',
                }}
            >
                <style>{`
                    @keyframes biDrawerSlideIn {
                        from { transform: translateX(20px); opacity: 0.4; }
                        to { transform: translateX(0); opacity: 1; }
                    }
                `}</style>
                <header style={{
                    background: 'linear-gradient(135deg, var(--ad-ink) 0%, #8b6b2c 60%, var(--ad-acc) 100%)',
                    color: '#fffbeb',
                    padding: '16px 20px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{title}</h2>
                    <button type="button" onClick={onClose} aria-label={t('Zatvori', 'Close')}
                        style={{ background: 'transparent', color: '#fffbeb', border: 0, fontSize: '1.7rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
                </header>
                <form onSubmit={onSave} style={{ padding: '18px 20px', display: 'grid', gap: 12 }}>
                    <label className="filter-label">{t('Tvrtka', 'Company')}
                        <select value={companyId} onChange={(e) => onCompanyIdChange(e.target.value)} required disabled={mode === 'edit' || busy}>
                            {companies.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                        </select>
                    </label>
                    <label className="filter-label">{t('Sektor (opcionalno)', 'Sector (optional)')}
                        <select value={sectorId} onChange={(e) => onSectorIdChange(e.target.value)} disabled={busy}>
                            <option value="">{t('Bez sektora', 'No sector')}</option>
                            {sectors.map((s) => <option key={s.id} value={s.id}>{s.code} — {s.name_hr}</option>)}
                        </select>
                    </label>
                    <label className="filter-label">{t('Kod', 'Code')}
                        <input value={code} onChange={(e) => onCodeChange(e.target.value)} required disabled={busy} />
                    </label>
                    <label className="filter-label">{t('Naziv', 'Name')}
                        <input value={name} onChange={(e) => onNameChange(e.target.value)} required disabled={busy} />
                    </label>
                    <label className="filter-label">{t('Regija', 'Region')}
                        <input value={region} onChange={(e) => onRegionChange(e.target.value)} list="bi-dept-regions-drawer" disabled={busy}
                            placeholder={t('npr. Sjever, Centar, Jug…', 'e.g. North, Center, South…')} />
                        <datalist id="bi-dept-regions-drawer">
                            {regionSuggestions.map((r) => <option key={r} value={r} />)}
                        </datalist>
                    </label>
                    <label className="filter-label">{t('Grad', 'City')}
                        <input value={city} onChange={(e) => onCityChange(e.target.value)} list="bi-dept-cities-drawer" disabled={busy}
                            placeholder={t('npr. Zagreb, Split…', 'e.g. Zagreb, Split…')} />
                        <datalist id="bi-dept-cities-drawer">
                            {citySuggestions.map((c) => <option key={c} value={c} />)}
                        </datalist>
                    </label>
                    <div className="admin-toolbar" style={{ marginTop: 8, flexWrap: 'wrap' }}>
                        <button className="compact" type="submit" disabled={busy}>{busy ? '⏳' : t('Spremi', 'Save')}</button>
                        <button className="secondary compact" type="button" onClick={onClose} disabled={busy}>{t('Odustani', 'Cancel')}</button>
                        {mode === 'edit' && onDelete ? (
                            // Delete uz Odustani (lijevo) — NE marginLeft:auto da ne završi u kutu pod plutajućim AI/chat gumbima.
                            <button type="button" className="secondary compact" style={{ color: '#b14040' }}
                                disabled={busy} onClick={onDelete}>
                                🗑 {t('Obriši', 'Delete')}
                            </button>
                        ) : null}
                    </div>
                </form>
            </aside>
        </div>
    );
}

function UserDetailDrawer({
    user, currentUser, locale, onClose,
    onEdit, onAssignments, onPreview, onToggleDirectory,
    onSetStatus, onImpersonate, onRestore, onRemove,
}: {
    user: BiAdminUser | null;
    currentUser: User;
    locale: 'hr' | 'en';
    onClose: () => void;
    onEdit: (u: BiAdminUser) => void;
    onAssignments: (u: BiAdminUser) => void;
    onPreview: (id: number) => void;
    onToggleDirectory: (u: BiAdminUser) => void;
    onSetStatus: (id: number, s: 'pending' | 'active' | 'inactive') => void;
    onImpersonate: (u: BiAdminUser) => void;
    onRestore: (id: number) => void;
    onRemove: (id: number) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    React.useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') onClose();
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (!user) return null;
    const isArchived = !!(user as { deleted_at?: string | null }).deleted_at;
    const status = user.status ?? 'active';
    const isSelf = user.id === currentUser.id;
    const statusStyle: Record<string, { bg: string; fg: string; label: string }> = {
        pending: { bg: 'rgba(var(--ad-acc-rgb),0.18)', fg: 'var(--ad-acc-3)', label: t('Na čekanju', 'Pending') },
        active: { bg: 'rgba(31,122,54,0.15)', fg: '#1f7a36', label: t('Aktivan', 'Active') },
        inactive: { bg: 'rgba(0,0,0,0.08)', fg: '#666', label: t('Neaktivan', 'Inactive') },
    };
    const st = statusStyle[status] ?? statusStyle.active;

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(20,16,8,0.32)',
                backdropFilter: 'blur(2px)',
                zIndex: 90,
                display: 'flex',
                justifyContent: 'flex-end',
            }}
        >
            <aside
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: 'min(440px, 96vw)',
                    height: '100%',
                    background: 'linear-gradient(180deg, #fffdf6 0%, var(--ad-bg) 100%)',
                    boxShadow: '-20px 0 60px rgba(107,79,28,0.22), -1px 0 0 rgba(var(--ad-soft-rgb),0.18)',
                    overflowY: 'auto',
                    animation: 'biDrawerSlideIn 0.18s ease-out',
                }}
            >
                <style>{`
                    @keyframes biDrawerSlideIn {
                        from { transform: translateX(20px); opacity: 0.4; }
                        to { transform: translateX(0); opacity: 1; }
                    }
                `}</style>
                <header style={{
                    background: 'linear-gradient(135deg, var(--ad-ink) 0%, #8b6b2c 60%, var(--ad-acc) 100%)',
                    color: '#fffbeb',
                    padding: '16px 20px',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                }}>
                    <div style={{ display: 'flex', gap: 14, alignItems: 'center', flex: 1, minWidth: 0 }}>
                        <Avatar
                            user={{ name: user.name, profile: { photo_url: user.photo_url ?? null } }}
                            size={56}
                        />
                        <div style={{ minWidth: 0 }}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>{user.name}</h2>
                            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: 'rgba(255,251,235,0.82)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email ?? '—'}</p>
                            <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ background: st.bg, color: st.fg, padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700 }}>{st.label}</span>
                                <span style={{ background: 'rgba(255,251,235,0.18)', color: '#fffbeb', padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600 }}>{user.role}</span>
                                {user.is_protected ? <span style={{ background: 'rgba(255,251,235,0.18)', color: '#fffbeb', padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600 }}>🛡 {t('Zaštićen', 'Protected')}</span> : null}
                                {user.show_in_directory ? <span style={{ background: 'rgba(255,251,235,0.18)', color: '#fffbeb', padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600 }}>📖 {t('U imeniku', 'In directory')}</span> : null}
                                {isArchived ? <span style={{ background: 'rgba(177,64,64,0.28)', color: '#fff', padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700 }}>🗑 {t('Arhiviran', 'Archived')}</span> : null}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('Zatvori', 'Close')}
                        style={{ background: 'transparent', color: '#fffbeb', border: 0, fontSize: '1.7rem', cursor: 'pointer', lineHeight: 1, padding: '2px 6px' }}
                    >×</button>
                </header>

                <div style={{ padding: '16px 20px' }}>
                    <h3 style={{ margin: '0 0 8px', fontSize: '0.74rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>{t('Pozicija', 'Position')}</h3>
                    <dl style={{ margin: '0 0 16px', display: 'grid', gridTemplateColumns: '110px 1fr', gap: '4px 12px', fontSize: '0.86rem' }}>
                        <dt className="muted">{t('Radno mjesto', 'Job title')}</dt><dd>{user.job_title ?? <span className="muted">—</span>}</dd>
                        <dt className="muted">{t('Tvrtka', 'Company')}</dt><dd>{user.company_label ?? <span className="muted">—</span>}</dd>
                        <dt className="muted">{t('Poslovnica', 'Branch')}</dt><dd>{user.primary_department_label ?? <span className="muted">—</span>}</dd>
                        <dt className="muted">📞 {t('Telefon', 'Phone')}</dt><dd>{user.phone_business ?? <span className="muted">—</span>}</dd>
                        <dt className="muted">{t('Zadnja aktivnost', 'Last activity')}</dt><dd>{user.last_activity_at ? formatDateTime(user.last_activity_at, locale) : <span className="muted">{t('nikad', 'never')}</span>}</dd>
                    </dl>

                    {isArchived ? (
                        <button
                            type="button"
                            onClick={() => onRestore(user.id)}
                            style={{ width: '100%', background: '#1f7a36', color: '#fff', border: 0, padding: '10px 14px', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}
                        >
                            ↺ {t('Vrati iz arhive', 'Restore from archive')}
                        </button>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <h3 style={{ margin: '8px 0 4px', fontSize: '0.74rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>{t('Akcije', 'Actions')}</h3>

                            {status === 'pending' ? (
                                <DrawerActionButton onClick={() => onSetStatus(user.id, 'active')} icon="✓" label={t('Odobri korisnika', 'Approve user')} variant="primary" />
                            ) : null}
                            {status === 'active' && !isSelf ? (
                                <DrawerActionButton onClick={() => onSetStatus(user.id, 'inactive')} icon="⏸" label={t('Deaktiviraj', 'Deactivate')} />
                            ) : null}
                            {status === 'inactive' ? (
                                <DrawerActionButton onClick={() => onSetStatus(user.id, 'active')} icon="▶" label={t('Aktiviraj', 'Activate')} variant="primary" />
                            ) : null}

                            <DrawerActionButton onClick={() => onEdit(user)} icon="✎" label={t('Uredi profil', 'Edit profile')} />
                            <DrawerActionButton onClick={() => onAssignments(user)} icon="🔑" label={t('Dodjele i ovlasti', 'Assignments & access')} />
                            <DrawerActionButton onClick={() => onPreview(user.id)} icon="👁" label={t('Pregledaj profil', 'Preview profile')} />
                            <DrawerActionButton
                                onClick={() => onToggleDirectory(user)}
                                icon={user.show_in_directory ? '📕' : '📖'}
                                label={user.show_in_directory ? t('Ukloni iz imenika', 'Remove from directory') : t('Dodaj u imenik', 'Add to directory')}
                            />
                            <DrawerActionButton
                                onClick={() => onImpersonate(user)}
                                icon="🎭"
                                label={t('Impersoniraj', 'Impersonate')}
                                disabled={!user.is_active || isSelf}
                            />

                            <div style={{ marginTop: 10, borderTop: '1px solid rgba(var(--ad-soft-rgb),0.2)', paddingTop: 10 }}>
                                <DrawerActionButton
                                    onClick={() => onRemove(user.id)}
                                    icon="🗑"
                                    label={t('Obriši korisnika', 'Delete user')}
                                    variant="danger"
                                    disabled={user.is_protected || isSelf}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </aside>
        </div>
    );
}

function DrawerActionButton({
    onClick, icon, label, variant, disabled,
}: {
    onClick: () => void;
    icon: string;
    label: string;
    variant?: 'primary' | 'danger';
    disabled?: boolean;
}) {
    const palette =
        variant === 'primary'
            ? { bg: 'linear-gradient(135deg, var(--ad-ink) 0%, var(--ad-acc) 100%)', fg: '#fffbeb', border: 'transparent' }
            : variant === 'danger'
                ? { bg: 'rgba(177,64,64,0.08)', fg: '#b14040', border: 'rgba(177,64,64,0.35)' }
                : { bg: '#fff', fg: '#34383d', border: 'rgba(var(--ad-soft-rgb),0.32)' };
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', textAlign: 'left',
                padding: '10px 12px',
                background: palette.bg,
                color: palette.fg,
                border: `1px solid ${palette.border}`,
                borderRadius: 10,
                fontSize: '0.88rem',
                fontWeight: 600,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.45 : 1,
                transition: 'transform 0.1s ease',
            }}
        >
            <span style={{ fontSize: '1.05rem' }}>{icon}</span>
            <span style={{ flex: 1 }}>{label}</span>
        </button>
    );
}
